import { useIOSPresentation } from '../../lib/web-presentation';
import { IOSDisclosure, IOSNavigation } from '../../ios-web/components';
import { Children, isValidElement, useEffect, useId, useLayoutEffect, useRef, useState, type ChangeEvent, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Button, Cluster, DataTable, EmptyState, ErrorState, Field, Icon, MetricCard, MetricGrid, Panel, Select, Stack, StatusBadge, ThemePreferenceSelector, Toggle } from '../../components';
import { accountInitials, useWorkspaceAccount } from '../../components/WorkspaceAccountContext';
import { useWorkspaceChromeHosts } from '../../components/WorkspaceChromeContext';
import { useStandardMenuContext } from '../../components/StandardMenu';
import type { SharedJourneyPorts } from './ports';
import type { BridgeState, CalculationSnapshot, EvidenceActivityItem, EvidenceDispositionCode, EvidenceDocumentView, EvidenceItem, GeneratedIntelligenceAnswer, HumanEvidenceClassificationInput, IdentityContext, IntelligenceNavigationIntent, IntelligenceState, ReconciliationCorrection, ReconciliationItem, ResponsibilityStep, SubmissionEvent, WorkspaceNavigationItem } from './types';
import { activeReconciliationForEvidence } from './evidence-disposition';
import { DocumentUploadDialog } from './document-upload-dialog';
import { presentAssistantActivity, presentAssistantAnswerBlocks, presentAssistantError,
  askAmoyeDataUseHref, presentAssistantLimitations, presentAssistantMethod, presentCitation } from './intelligence-presentation';
import { intelligenceEvidenceSourcesEvent } from './intelligence-navigation';
import './styles.css';

const toneFor = (state: string): 'neutral' | 'info' | 'success' | 'warning' | 'danger' => state.includes('blocked') || state.includes('failed') ? 'danger' : state.includes('ready') || state === 'current' ? 'info' : state === 'complete' || state === 'reviewed' || state.includes('received') ? 'success' : state === 'ambiguous' || state === 'waiting' || state === 'needs-review' ? 'warning' : 'neutral';

export function AssistantAnswerContent({ answer }: { answer: string }) {
  return <div className="am-intelligence-answer-content">{presentAssistantAnswerBlocks(answer).map((block, index) => {
    const key = `${block.kind}:${index}`;
    if (block.kind === 'heading') return <h4 key={key}>{block.text}</h4>;
    if (block.kind === 'paragraph') return <p key={key}>{block.text}</p>;
    const items = block.items.map((item, itemIndex) => <li key={`${item}:${itemIndex}`}>{item}</li>);
    return block.ordered
      ? <ol className="am-intelligence-answer__list" key={key}>{items}</ol>
      : <ul className="am-intelligence-answer__list" key={key}>{items}</ul>;
  })}</div>;
}

export function workflowStateLabel(state: string): string {
  const labels: Record<string, string> = {
    'ready-for-mock-request': 'ready for delivery test',
    'validated-for-mock': 'checks passed',
    'queued-to-bridge-mock': 'queued for delivery test',
    'bridge-mock-received': 'test received',
    'bridge-mock-only': 'test delivery only',
    'signed-for-mock-request': 'signed',
    'standards-bridge-mock-only': 'test delivery only',
    'processing-failed': 'processing failed',
    'processing-delayed': 'processing delayed',
    checking: 'queued / processing',
    'excluded-by-human': 'not used',
    unlinked: 'removed',
  };
  return labels[state] ?? state.replaceAll('-', ' ');
}

export interface JourneyFrameProps { children: ReactNode; label: string; }
export function JourneyFrame({ children, label }: JourneyFrameProps) {
  const elements = Children.toArray(children);
  const navigation = elements.find((child) => isValidElement(child) && child.type === WorkspaceNavigation);
  const content = elements.filter((child) => child !== navigation);
  return <div aria-label={label} className="am-feature-journey">
    {navigation}<div className="am-feature-journey__content">{content}</div>
  </div>;
}

export interface IdentityContextSwitcherProps {
  contexts: readonly IdentityContext[];
  currentContextId: string;
  ports: Pick<SharedJourneyPorts, 'switchContext'>;
}
export function IdentityContextSwitcher({ contexts, currentContextId, ports }: IdentityContextSwitcherProps) {
  const current = contexts.find((context) => context.id === currentContextId) ?? contexts[0];
  const [capacity, setCapacity] = useState(current?.capacity ?? 'individual');
  useEffect(() => { if (current) setCapacity(current.capacity); }, [current?.id, current?.capacity]);
  const changeContext = (event: ChangeEvent<HTMLSelectElement>) => {
    const selected = contexts.find((context) => context.id === event.target.value);
    if (!selected) return;
    setCapacity(selected.capacity);
    void ports.switchContext({ contextId: selected.id, capacity: selected.capacity });
  };
  return <Panel className="am-context-panel" title="Your working context" description="The same identity can act in different products. Every action remains limited to this visible context.">
    <div className="am-context-grid">
      <Field label="Workspace and taxpayer"><Select value={current?.id ?? ''} onChange={changeContext}>{contexts.map((context) => <option key={context.id} value={context.id}>{context.workspaceName} — {context.subjectName}</option>)}</Select></Field>
      <Field label="Acting capacity"><Select onChange={(event) => { const next = event.target.value as IdentityContext['capacity']; setCapacity(next); if (current) void ports.switchContext({ contextId: current.id, capacity: next }); }} value={capacity}>{[...new Set(contexts.map((context) => context.capacity))].map((item) => <option key={item} value={item}>{item.replaceAll('-', ' ')}</option>)}</Select></Field>
    </div>
    {current && <dl className="am-context-summary"><div><dt>Taxpayer</dt><dd>{current.subjectName}</dd></div><div><dt>Period</dt><dd>{current.periodLabel}</dd></div><div><dt>Package</dt><dd>{current.packageLabel}</dd></div><div><dt>Assignment</dt><dd>{current.assignmentLabel ?? 'Own tax position'}</dd></div></dl>}
  </Panel>;
}

export interface EvidenceReviewProps {
  context: IdentityContext;
  evidence: readonly EvidenceItem[];
  reconciliation: readonly ReconciliationItem[];
  ports: Pick<SharedJourneyPorts, 'registerEvidence' | 'retryEvidenceProcessing' | 'reviewReconciliation' | 'unlinkEvidence' | 'viewEvidence' | 'reviewEvidenceOwnership' | 'humanClassifyEvidence' | 'loadEvidenceActivity'>;
  initialView?: 'documents' | 'activity';
  showUploader?: boolean;
}
export function EvidenceReview({ context, evidence, reconciliation, ports, initialView = 'documents', showUploader = true }: EvidenceReviewProps) {
  const ios = useIOSPresentation();
  const collection = context.track === 'B2' ? 'Documents' : 'Records';
  const [evidenceView, setEvidenceView] = useState<'documents' | 'activity'>(initialView);
  const [viewing, setViewing] = useState<EvidenceItem>();
  const [selectedEvidenceIds, setSelectedEvidenceIds] = useState<ReadonlySet<string>>(new Set());
  const [bulkRemoveOpen, setBulkRemoveOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkEvidenceResult>();
  const bulkOperationSequence = useRef(0);
  useEffect(() => {
    const openSources = () => setEvidenceView('documents');
    window.addEventListener(intelligenceEvidenceSourcesEvent, openSources);
    return () => window.removeEventListener(intelligenceEvidenceSourcesEvent, openSources);
  }, []);
  const currentContextId = useRef(context.id);
  currentContextId.current = context.id;
  const [reviewing, setReviewing] = useState<ReadonlySet<string>>(new Set());
  const [reviewErrors, setReviewErrors] = useState<Readonly<Record<string, string>>>({});
  const [correction, setCorrection] = useState<{ itemId: string; ntaCode: string; vatNaira: string;
    evidenceCategory: string }>();
  const decide = async (item: ReconciliationItem, decision: 'accept' | 'correct' | 'exclude',
    corrected?: ReconciliationCorrection) => {
    setReviewing((current) => new Set(current).add(item.id));
    setReviewErrors((current) => { const next = { ...current }; delete next[item.id]; return next; });
    try {
      if (corrected) await ports.reviewReconciliation(item, decision, corrected);
      else await ports.reviewReconciliation(item, decision);
      if (decision === 'correct') setCorrection(undefined);
    }
    catch (error) { setReviewErrors((current) => ({ ...current,
      [item.id]: error instanceof Error ? error.message : 'The review decision could not be recorded.' })); }
    finally { setReviewing((current) => { const next = new Set(current); next.delete(item.id); return next; }); }
  };
  const removedEvidence = evidence.filter((item) => item.state === 'unlinked' || item.state === 'superseded');
  const activeEvidence = evidence.filter((item) => item.state !== 'unlinked' && item.state !== 'superseded');
  const activeReconciliation = activeReconciliationForEvidence(evidence, reconciliation);
  const selectedEvidence = evidence.filter((item) => selectedEvidenceIds.has(item.id));
  const everySelectedCanResume = selectedEvidence.length > 0 && Boolean(ports.retryEvidenceProcessing)
    && selectedEvidence.every((item) => item.processingRetryable !== false && ['processing-failed', 'processing-delayed'].includes(item.state));
  useEffect(() => {
    bulkOperationSequence.current += 1;
    setSelectedEvidenceIds(new Set());
    setBulkRemoveOpen(false);
    setBulkBusy(false);
    setBulkResult(undefined);
  }, [context.id]);
  useEffect(() => {
    const activeIds = new Set(activeEvidence.map((item) => item.id));
    setSelectedEvidenceIds((current) => {
      const next = new Set([...current].filter((id) => activeIds.has(id)));
      return next.size === current.size && [...next].every((id) => current.has(id)) ? current : next;
    });
  }, [evidence]);
  useEffect(() => { if (bulkRemoveOpen && selectedEvidence.length === 0) setBulkRemoveOpen(false); }, [bulkRemoveOpen, selectedEvidence.length]);
  const toggleEvidence = (id: string, selected: boolean) => {
    setBulkResult(undefined);
    setSelectedEvidenceIds((current) => {
      const next = new Set(current);
      if (selected) next.add(id); else next.delete(id);
      return next;
    });
  };
  const selectAll = <EvidenceSelectAll selected={selectedEvidenceIds.size} total={activeEvidence.length}
    onChange={(checked) => { setBulkResult(undefined); setSelectedEvidenceIds(checked
      ? new Set(activeEvidence.map((item) => item.id)) : new Set()); }} />;
  const sourceActions = (item: EvidenceItem) => <EvidenceSourceActions item={item}
    onRetry={ports.retryEvidenceProcessing ? () => ports.retryEvidenceProcessing!(item) : undefined}
    onReviewOwnership={(decision) => ports.reviewEvidenceOwnership(item, decision)} onView={() => setViewing(item)}
    onHumanClassify={(input) => ports.humanClassifyEvidence(item, input)}
    onUnlink={(input) => ports.unlinkEvidence(item, input)} />;
  const runBulk = async (items: readonly EvidenceItem[], operation: 'remove' | 'resume',
    input?: { reason: EvidenceDispositionCode; note?: string }) => {
    const operationSequence = ++bulkOperationSequence.current;
    const operationContextId = context.id;
    setBulkBusy(true); setBulkResult(undefined);
    const failures: BulkEvidenceFailure[] = [];
    const batchId = operation === 'remove' && items.length > 1 ? globalThis.crypto.randomUUID() : undefined;
    let succeeded = 0;
    for (const item of items) {
      if (currentContextId.current !== operationContextId || bulkOperationSequence.current !== operationSequence) return;
      try {
        if (operation === 'remove') {
          if (!input) throw new Error('Choose why the selected sources should be removed.');
          await ports.unlinkEvidence(item, { ...input, ...(batchId ? { batchId } : {}) });
        }
        else await ports.retryEvidenceProcessing!(item);
        succeeded += 1;
      } catch (error) {
        failures.push({ id: item.id, name: item.name,
          message: error instanceof Error ? error.message : `The source could not be ${operation === 'remove' ? 'removed' : 'resumed'}.` });
      }
    }
    if (currentContextId.current !== operationContextId || bulkOperationSequence.current !== operationSequence) return;
    setSelectedEvidenceIds(new Set(failures.map((failure) => failure.id)));
    setBulkResult({ operation, requested: items.length, succeeded, failures });
    setBulkBusy(false);
    if (operation === 'remove') setBulkRemoveOpen(false);
  };
  return <><nav aria-label={ios ? 'Evidence views' : `${collection} views`} className="am-evidence-view-tabs">
    <button aria-current={evidenceView === 'documents' ? 'page' : undefined}
      onClick={() => setEvidenceView('documents')} type="button">{ios ? 'Documents' : collection}</button>
    <button aria-current={evidenceView === 'activity' ? 'page' : undefined}
      onClick={() => setEvidenceView('activity')} type="button">{ios ? 'Activity' : 'Audit trail'}</button>
  </nav>{evidenceView === 'activity'
    ? <EvidenceActivityView context={context} key={context.id} loadActivity={ports.loadEvidenceActivity} />
    : <><Stack>
    <Panel id="evidence-upload" tabIndex={-1} title={ios ? 'Source documents' : collection}
      actions={ios && showUploader ? <DocumentUploadDialog context={context}
        onRegister={(file, options) => ports.registerEvidence(file, context, options)} /> : undefined}
      description={ios ? `Documents for ${context.subjectName}. Review each source before using its figures.`
        : `Every file on this page is scoped to ${context.subjectName}. Original files remain separate from extracted proposals and reviewed facts.`}>
      {showUploader && !ios && <div className="am-upload-stack">
        <div className="am-client-scope-banner" role="status"><span>Uploading for</span><strong>{context.subjectName}</strong><small>{context.packageLabel} · {context.periodLabel}</small></div>
        <div className="am-upload-launcher"><div><strong>Add supporting documents</strong><p>Choose one document or add several with multi-file selection or drag and drop.</p></div>
          <DocumentUploadDialog context={context} onRegister={(file, options) => ports.registerEvidence(file, context, options)} /></div>
      </div>}
      {selectedEvidence.length > 0 && <div aria-label={ios ? 'Bulk evidence actions' : 'Selected document actions'} className="am-evidence-bulk-actions" role="region">
        <div><strong>{selectedEvidence.length} {ios ? 'evidence source' : 'source document'}{selectedEvidence.length === 1 ? '' : 's'} selected</strong>
          <small>Actions apply only to the selected sources in {context.subjectName}’s current tax record.</small></div>
        <Cluster>{everySelectedCanResume && <Button disabled={bulkBusy} loading={bulkBusy}
          onClick={() => void runBulk(selectedEvidence, 'resume')} size="small">Resume selected</Button>}
          <Button disabled={bulkBusy} onClick={() => setBulkRemoveOpen(true)} size="small" tone="danger">Delete selected</Button>
          <Button disabled={bulkBusy} onClick={() => setSelectedEvidenceIds(new Set())} size="small" tone="secondary">Clear selection</Button></Cluster>
      </div>}
      {bulkResult && <BulkEvidenceResultNotice result={bulkResult} />}
      <section aria-label={ios ? 'Active evidence' : `Active ${collection.toLowerCase()}`} id="evidence-sources" tabIndex={-1}>{ios ? <>
        {activeEvidence.length > 0 ? <>
          <div className="ios-evidence-toolbar"><label>{selectAll}<span>Select all</span></label>
            <span>{activeEvidence.length} document{activeEvidence.length === 1 ? '' : 's'}</span></div>
          <ul aria-label="Evidence documents" className="ios-evidence-list">{activeEvidence.map((item) => <li
            className="ios-evidence-card" key={item.id}>
            <header className="ios-evidence-card__header">
              <label className="ios-evidence-card__select"><input aria-label={`Select ${item.name}`}
                checked={selectedEvidenceIds.has(item.id)} className="am-evidence-select"
                onChange={(event) => toggleEvidence(item.id, event.target.checked)} type="checkbox" /></label>
              <div><h3>{item.name}</h3><p>{item.sourceType.replaceAll('-', ' ')} · {item.taxpayerName ?? context.subjectName}</p></div>
            </header>
            <div className="ios-evidence-card__assessment">
              <StatusBadge tone={toneFor(item.state)}>{workflowStateLabel(item.state)}</StatusBadge>
              {item.issue && <p>{item.issue}</p>}
            </div>
            <div className="ios-evidence-card__actions">{sourceActions(item)}</div>
            <IOSDisclosure title="Document details"><dl className="ios-evidence-details">
              <div><dt>Bound taxpayer</dt><dd>{item.taxpayerName ?? context.subjectName}</dd></div>
              {item.ownershipState && <div><dt>Ownership review</dt><dd>{item.ownershipState.replaceAll('-', ' ')}</dd></div>}
              {item.ownershipProposal?.detectedName && <div><dt>Detected owner</dt><dd>{item.ownershipProposal.detectedName}</dd></div>}
              <div><dt>Source fingerprint</dt><dd>{item.hashLabel}</dd></div>
              <div><dt>Added by</dt><dd>{item.addedBy}</dd></div>
              <div><dt>Added</dt><dd>{item.addedAt}</dd></div>
            </dl></IOSDisclosure>
          </li>)}</ul>
        </> : <EmptyState compact title="No active evidence" description="Add a document. If this workspace has legacy removals, expand Previously removed evidence to delete them permanently." />}
      </> : <>
        <div className="am-evidence-list-toolbar"><label>{selectAll}<span>Select all</span></label>
          <span>{activeEvidence.length} document{activeEvidence.length === 1 ? '' : 's'}</span></div>
        {activeEvidence.length > 0 ? <ul aria-label={`${collection} currently used or awaiting a decision`}
          className="am-evidence-document-list">{activeEvidence.map((item) => <li className="am-evidence-document-card" key={item.id}>
          <header><label className="am-evidence-document-card__select"><input aria-label={`Select ${item.name}`}
            checked={selectedEvidenceIds.has(item.id)} className="am-evidence-select"
            onChange={(event) => toggleEvidence(item.id, event.target.checked)} type="checkbox" /></label>
            <div className="am-evidence-document-card__title"><h3>{item.name}</h3>
              <p>{item.sourceType.replaceAll('-', ' ')} · {item.hashLabel}</p></div>
            <StatusBadge tone={toneFor(item.state)}>{workflowStateLabel(item.state)}</StatusBadge></header>
          <dl className="am-evidence-document-card__details"><div><dt>Bound taxpayer</dt>
            <dd><strong>{item.taxpayerName ?? context.subjectName}</strong>
              {item.ownershipState && <small>{item.ownershipState.replaceAll('-', ' ')}</small>}
              {item.ownershipProposal?.detectedName && <small>Detected owner: {item.ownershipProposal.detectedName}</small>}</dd></div>
            <div><dt>Added</dt><dd><strong>{item.addedAt}</strong><small>By {item.addedBy}</small></dd></div>
            <div className="am-evidence-document-card__next"><dt>Next step</dt>
              <dd>{item.issue ?? 'No source issue is currently recorded.'}</dd></div></dl>
          <footer>{sourceActions(item)}</footer>
        </li>)}</ul> : <EmptyState compact title={`No active ${collection.toLowerCase()}`}
          description={`Add a document. If this workspace has legacy removals, expand Previously removed ${collection.toLowerCase()} to delete them permanently.`} />}
      </>}</section>
      {removedEvidence.length > 0 && <details className="am-removed-evidence">
        <summary><span>{ios ? 'Previously removed evidence' : `Previously removed ${collection.toLowerCase()}`}</span><StatusBadge tone="neutral">{removedEvidence.length}</StatusBadge></summary>
        <div><p>These sources were removed using the former unlink-only behavior. Delete them permanently to purge their stored files and prevent recovery after sign-in.</p>
          <Button disabled={bulkBusy} onClick={() => { setSelectedEvidenceIds(new Set(removedEvidence.map((item) => item.id)));
            setBulkRemoveOpen(true); }} size="small" tone="danger">{ios ? 'Delete all previously removed evidence' : `Delete all previously removed ${collection.toLowerCase()}`}</Button>
          <ul aria-label={ios ? 'Evidence retained outside the active tax record' : `${collection} retained outside the active tax record`} className="am-evidence-document-list am-evidence-document-list--removed">
            {removedEvidence.map((item) => <li className="am-evidence-document-card" key={item.id}><header>
              <div className="am-evidence-document-card__title"><h3>{item.name}</h3><p>{item.hashLabel}</p></div>
              <StatusBadge tone="neutral">previously removed</StatusBadge></header>
              <dl className="am-evidence-document-card__details"><div><dt>Removal reason</dt>
                <dd><strong>{item.disposition?.code.replaceAll('-', ' ') ?? item.state}</strong>
                  {item.disposition?.note && <small>{item.disposition.note}</small>}</dd></div>
                <div><dt>Recorded by</dt><dd><strong>{item.disposition?.recordedBy ?? item.addedBy}</strong>
                  {item.disposition?.recordedAt && <small>{new Date(item.disposition.recordedAt).toLocaleString('en-NG')}</small>}</dd></div></dl>
              <footer><EvidenceSourceActions item={item}
                onReviewOwnership={(decision) => ports.reviewEvidenceOwnership(item, decision)} onView={() => setViewing(item)}
                onHumanClassify={(input) => ports.humanClassifyEvidence(item, input)}
                onUnlink={(input) => ports.unlinkEvidence(item, input)} /></footer>
            </li>)}</ul></div>
      </details>}
    </Panel>
    <Panel id="evidence-review" tabIndex={-1} title="Reconciliation review" description="Review each proposal against its source, then accept, correct or exclude it.">
      {activeReconciliation.length === 0 ? <p>No items to review.</p> : <ul className="am-reconciliation-list" aria-label="Items requiring human judgement">
        {activeReconciliation.map((item) => {
          const pending = reviewing.has(item.id); const error = reviewErrors[item.id];
          return <li key={item.id} className="am-reconciliation-item">
            <header><div><h3>{item.description}</h3><small className="am-feature-muted">{item.sourceLabel}</small></div>
              <div className="am-reconciliation-item__state"><StatusBadge tone={toneFor(item.state)}>{workflowStateLabel(item.state)}</StatusBadge></div></header>
            <dl><div><dt>Amount</dt><dd>{item.amountLabel}</dd></div>
              <div><dt>Suggested treatment</dt><dd>{item.proposedTreatment}</dd></div>
              <div><dt>Confidence</dt><dd><StatusBadge tone={item.confidence === 'low' ? 'warning' : 'info'}>{item.confidence}</StatusBadge></dd></div></dl>
            <p>{item.reason}</p><Cluster>
            <Button disabled={item.state !== 'needs-review' || pending} loading={pending}
              onClick={() => void decide(item, 'accept')} size="small">Accept</Button>
            <Button disabled={item.state !== 'needs-review' || pending} onClick={() => setCorrection({ itemId: item.id,
              ntaCode: item.ntaCode ?? '', vatNaira: item.vatAmountKobo ? koboToNairaInput(item.vatAmountKobo) : '',
              evidenceCategory: item.evidenceCategory ?? '' })}
              size="small" tone="secondary">Classify or correct</Button>
            <Button disabled={item.state !== 'needs-review' || pending} onClick={() => void decide(item, 'exclude')}
              size="small" tone="secondary">Not this transaction</Button></Cluster>
            {correction?.itemId === item.id && <CorrectionEditor item={item} value={correction}
              onCancel={() => setCorrection(undefined)} onChange={setCorrection}
              onSave={(value) => void decide(item, 'correct', value)} />}
            {error && <small className="am-review-decision-error" role="alert">{error}</small>}</li>;
        })}
      </ul>}
    </Panel>
  </Stack>{viewing && <EvidenceDocumentViewer item={viewing} load={() => ports.viewEvidence(viewing)} onClose={() => setViewing(undefined)} />}
    {bulkRemoveOpen && <BulkEvidenceRemovalDialog busy={bulkBusy} context={context} items={selectedEvidence}
      onCancel={() => setBulkRemoveOpen(false)} onConfirm={(input) => void runBulk(selectedEvidence, 'remove', input)} />}</>}</>;
}

function EvidenceActivityView({ context, loadActivity }: { context: IdentityContext;
  loadActivity?: SharedJourneyPorts['loadEvidenceActivity'] }) {
  const ios = useIOSPresentation();
  const [limit, setLimit] = useState(20);
  const [events, setEvents] = useState<readonly EvidenceActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError('');
    if (!loadActivity) {
      setEvents([]); setLoading(false);
      return () => controller.abort();
    }
    void loadActivity(limit, controller.signal).then((next) => {
      if (!controller.signal.aborted) setEvents(next);
    }).catch((caught: unknown) => {
      if (controller.signal.aborted || caught instanceof DOMException && caught.name === 'AbortError') return;
      setError('We could not load this activity. Please try again.');
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [context.id, limit, loadActivity, reload]);

  return <Panel className="am-evidence-activity" title={ios ? 'Evidence activity' : 'Audit trail'}
    description={`A record of document actions for ${context.subjectName}, ${context.periodLabel}. Deleted documents appear only as redacted receipts.`}
    actions={<Button disabled={loading} onClick={() => setReload((value) => value + 1)} size="small" tone="secondary">Refresh</Button>}>
    {loading && events.length === 0 ? <div aria-live="polite" className="am-evidence-activity__loading" role="status">
      <span aria-hidden="true" />Loading activity…
    </div> : error ? <ErrorState title="Activity could not be loaded" description={error}
      action={<Button onClick={() => setReload((value) => value + 1)} size="small">Try again</Button>} />
      : events.length === 0 ? <EmptyState title={ios ? 'No evidence activity yet' : 'No document activity yet'}
        description="Actions such as adding or deleting documents will appear here for this taxpayer and filing period." />
        : <><ol aria-label={ios ? 'Evidence activity for the selected tax record' : 'Audit trail for the selected tax record'} className="am-evidence-activity__list">
          {events.map((event) => <EvidenceActivityReceipt context={context} event={event} key={event.id} />)}
        </ol>{events.length >= limit && limit < 100 && <div className="am-evidence-activity__more">
          <Button disabled={loading} loading={loading} onClick={() => setLimit((value) => Math.min(100, value + 20))}
            tone="secondary">Show earlier activity</Button>
        </div>}</>}
  </Panel>;
}

function EvidenceActivityReceipt({ context, event }: { context: IdentityContext; event: EvidenceActivityItem }) {
  const presentation = evidenceActivityPresentation(event);
  return <li className={`am-evidence-activity__item am-evidence-activity__item--${presentation.tone}`}>
    <span aria-hidden="true" className="am-evidence-activity__marker" />
    <div className="am-evidence-activity__summary">
      <div><strong>{event.actor_label} {presentation.action}</strong>
        <time dateTime={event.occurred_at}>{formatEvidenceActivityTime(event.occurred_at)}</time></div>
      <p>{presentation.detail}</p>
      {presentation.nextStep && <small><strong>Next:</strong> {presentation.nextStep}</small>}
      <details><summary>Receipt details</summary><dl>
        <div><dt>Tax record</dt><dd>{context.subjectName} · {context.periodLabel}</dd></div>
        <div><dt>Person or role</dt><dd>{plainCapacity(event.actor_capacity)}</dd></div>
        <div><dt>Result</dt><dd>{presentation.result}</dd></div>
        <div><dt>Receipt reference</dt><dd>{event.correlation_id}</dd></div>
        {event.batch_id && <div><dt>Group action reference</dt><dd>{event.batch_id}</dd></div>}
        <div><dt>Record check</dt><dd>{event.integrity === 'verified' ? 'Verified' : 'Needs administrator review'}</dd></div>
      </dl></details>
    </div>
  </li>;
}

function evidenceActivityPresentation(event: EvidenceActivityItem): { action: string; detail: string;
  nextStep?: string; result: string; tone: 'neutral' | 'success' | 'danger' } {
  switch (event.event_type) {
    case 'UPLOAD_ACCEPTED': return { action: 'added a document',
      detail: 'The document was added to this tax record.', nextStep: 'Review it in Documents.',
      result: 'Added', tone: 'success' };
    case 'DELETE_REQUESTED': return { action: 'requested document deletion',
      detail: 'The document was removed from use while permanent deletion is completed.',
      nextStep: 'No action is needed right now.', result: 'Deletion started', tone: 'neutral' };
    case 'DELETE_RETRY_REQUESTED': return { action: 'retried document deletion',
      detail: 'Permanent deletion was started again. The document remains unavailable.',
      nextStep: 'Check this activity again shortly.', result: 'Retry started', tone: 'neutral' };
    case 'DELETE_COMPLETED': return { action: 'completed document deletion',
      detail: 'The document and its usable results were permanently removed. Only this redacted receipt remains.',
      nextStep: 'If this document affected your tax position, review it before continuing.',
      result: 'Completed', tone: 'success' };
    case 'DELETE_FAILED': return { action: 'could not finish deleting a document',
      detail: 'The document remains unavailable and will not be used.',
      nextStep: 'Try the deletion again. If it still fails, contact support with the receipt reference.',
      result: 'Needs attention', tone: 'danger' };
  }
}

function formatEvidenceActivityTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? 'Time unavailable' : date.toLocaleString('en-NG', {
    dateStyle: 'medium', timeStyle: 'short',
  });
}

function plainCapacity(value: string): string {
  return value.split(/[-_]/).filter(Boolean).map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`).join(' ')
    || 'Authorised user';
}

interface BulkEvidenceFailure { id: string; name: string; message: string; }
interface BulkEvidenceResult {
  operation: 'remove' | 'resume';
  requested: number;
  succeeded: number;
  failures: readonly BulkEvidenceFailure[];
}

function EvidenceSelectAll({ selected, total, onChange }: { selected: number; total: number; onChange(checked: boolean): void }) {
  const ios = useIOSPresentation();
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (inputRef.current) inputRef.current.indeterminate = selected > 0 && selected < total; }, [selected, total]);
  return <input aria-label={`Select all ${total} active ${ios ? 'evidence sources' : 'source documents'}`} checked={total > 0 && selected === total}
    className="am-evidence-select" disabled={total === 0} onChange={(event) => onChange(event.target.checked)} ref={inputRef} type="checkbox" />;
}

function BulkEvidenceResultNotice({ result }: { result: BulkEvidenceResult }) {
  const verb = result.operation === 'remove' ? 'Deleted' : 'Resumed';
  return <div aria-live="polite" className={`am-evidence-bulk-result${result.failures.length ? ' has-failures' : ''}`}
    role={result.failures.length ? 'alert' : 'status'}>
    <strong>{verb} {result.succeeded} of {result.requested} selected source{result.requested === 1 ? '' : 's'}.</strong>
    {result.failures.length > 0 && <><span>{result.failures.length} source{result.failures.length === 1 ? '' : 's'} remain selected and unchanged:</span>
      <ul>{result.failures.map((failure) => <li key={failure.id}><strong>{failure.name}</strong>: {failure.message}</li>)}</ul></>}
  </div>;
}

function BulkEvidenceRemovalDialog({ busy, context, items, onCancel, onConfirm }: {
  busy: boolean;
  context: IdentityContext;
  items: readonly EvidenceItem[];
  onCancel(): void;
  onConfirm(input: { reason: EvidenceDispositionCode; note?: string }): void;
}) {
  const ios = useIOSPresentation();
  const [reason, setReason] = useState<EvidenceDispositionCode>('wrong-transaction');
  const [note, setNote] = useState('');
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    cancelRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape' && !busy) onCancel(); };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [busy, onCancel]);
  const count = items.length;
  return createPortal(<div className="am-document-dialog__backdrop"><section aria-labelledby="am-bulk-evidence-remove-title"
    aria-modal="true" className="am-evidence-bulk-dialog" role="dialog">
    <header><div><p className="am-eyebrow">Permanent deletion</p><h2 id="am-bulk-evidence-remove-title">Delete {count} {ios ? 'evidence source' : 'source document'}{count === 1 ? '' : 's'} permanently?</h2>
      <p>Only the {count} explicitly selected source{count === 1 ? '' : 's'} in {context.subjectName}’s {context.periodLabel} tax record will be deleted.</p></div></header>
    <div className="am-evidence-bulk-dialog__body">
      <label><span>Why should these sources not be used?</span><select disabled={busy} value={reason}
        onChange={(event) => setReason(event.target.value as EvidenceDispositionCode)}>
        <option value="wrong-transaction">Wrong transaction</option><option value="wrong-taxpayer">Wrong taxpayer</option>
        <option value="wrong-period">Wrong filing period</option><option value="unrelated">Not relevant</option>
        <option value="duplicate">Duplicate source</option>
      </select></label>
      <label><span>Note applied to all selected sources (optional)</span><textarea disabled={busy}
        onChange={(event) => setNote(event.target.value)} rows={3} value={note} /></label>
      <p>This deletes the stored files, removes their proposals from active and recoverable use, and invalidates dependent tax work. It cannot be undone. Non-viewable audit and provenance records remain, but they cannot restore the documents.</p>
      <details><summary>Review the {count} selected source{count === 1 ? '' : 's'}</summary>
        <ul>{items.map((item) => <li key={item.id}>{item.name}</li>)}</ul></details>
    </div>
    <footer><Cluster><Button disabled={busy || count === 0} loading={busy} onClick={() => onConfirm({ reason,
      ...(note.trim() ? { note: note.trim() } : {}) })} size="small" tone="danger">Delete {count} source{count === 1 ? '' : 's'} permanently</Button>
      <Button disabled={busy} onClick={onCancel} ref={cancelRef} size="small" tone="secondary">Cancel</Button></Cluster></footer>
  </section></div>, document.body);
}

const EVIDENCE_CATEGORIES = [
  'income', 'deductions_and_reliefs', 'tax_already_paid', 'identity_and_jurisdiction',
  'sales_and_output_vat', 'expense_and_input_vat', 'expense', 'payroll', 'mixed',
] as const;

const EVIDENCE_CATEGORY_LABELS: Record<(typeof EVIDENCE_CATEGORIES)[number], string> = {
  income: 'Income',
  deductions_and_reliefs: 'Deduction or relief',
  tax_already_paid: 'Tax already paid',
  identity_and_jurisdiction: 'Identity or tax jurisdiction',
  sales_and_output_vat: 'Sale and output VAT',
  expense_and_input_vat: 'Expense and input VAT',
  expense: 'Expense',
  payroll: 'Payroll',
  mixed: 'Mixed document — classify its individual items',
};

function CorrectionEditor({ item, value, onChange, onCancel, onSave }: {
  item: ReconciliationItem;
  value: { itemId: string; ntaCode: string; vatNaira: string; evidenceCategory: string };
  onChange(value: { itemId: string; ntaCode: string; vatNaira: string; evidenceCategory: string }): void;
  onCancel(): void;
  onSave(value: ReconciliationCorrection): void;
}) {
  const ios = useIOSPresentation();
  const [validationError, setValidationError] = useState<string>();
  const paths = item.serverProposals?.map((proposal) => proposal.fieldPath) ?? [];
  // Category is always reviewable. A low-confidence or unmatched proposal is
  // precisely where the human must be able to supply the classification.
  const editsCategory = true;
  const editsNta = paths.some((path) => path.endsWith('.nta_code')) || Boolean(item.ntaCode);
  const editsVat = paths.some((path) => path.endsWith('.vat_amount_kobo')) || item.vatAmountKobo !== undefined;
  return <div className="am-classification-correction" role="group" aria-label={`Correct ${item.description}`}>
    <strong>Classify this item</strong>
    <small>Choose how this item should be used. If one document contains income and deductions, classify its individual items; use Mixed only for the whole document. The original proposal remains in the audit trail.</small>
    {editsCategory && <label><span>{ios ? 'Evidence category' : 'Document category'}</span><select value={value.evidenceCategory}
      onChange={(event) => onChange({ ...value, evidenceCategory: event.target.value })}>
      <option value="">Choose a category</option>{EVIDENCE_CATEGORIES.map((category) => <option key={category} value={category}>
        {EVIDENCE_CATEGORY_LABELS[category]}</option>)}</select></label>}
    {editsNta && <label><span>Classification code</span><input maxLength={64} value={value.ntaCode}
      onChange={(event) => onChange({ ...value, ntaCode: event.target.value })} /></label>}
    {editsVat && <label><span>VAT amount (₦)</span><input inputMode="decimal" placeholder="0.00" value={value.vatNaira}
      onChange={(event) => onChange({ ...value, vatNaira: event.target.value })} /></label>}
    {validationError && <small className="am-review-decision-error" role="alert">{validationError}</small>}
    <Cluster><Button onClick={() => {
      try {
        const category = editsCategory ? requiredCorrection(value.evidenceCategory, ios ? 'Choose an evidence category.' : 'Choose a document category.') : undefined;
        const ntaCode = editsNta ? requiredCorrection(value.ntaCode, 'Enter a classification code.') : undefined;
        const vatAmountKobo = editsVat ? nairaInputToKobo(value.vatNaira) : undefined;
        const correction: ReconciliationCorrection = {
          ...(category !== undefined && category !== item.evidenceCategory ? { evidenceCategory: category } : {}),
          ...(ntaCode !== undefined && ntaCode !== item.ntaCode ? { ntaCode } : {}),
          ...(vatAmountKobo !== undefined && vatAmountKobo !== item.vatAmountKobo ? { vatAmountKobo } : {}),
        };
        if (Object.keys(correction).length === 0) throw new Error('Change at least one value, or use Accept to confirm Amoye’s proposal.');
        onSave(correction);
        setValidationError(undefined);
      } catch (error) {
        setValidationError(error instanceof Error ? error.message : 'Check the corrected values.');
      }
    }} size="small">Save correction</Button><Button onClick={onCancel} size="small" tone="secondary">Cancel</Button></Cluster>
  </div>;
}

function requiredCorrection(value: string, message: string): string {
  const normalized = value.trim();
  if (!normalized) throw new Error(message);
  return normalized;
}

function koboToNairaInput(kobo: string): string {
  const amount = BigInt(kobo); const absolute = amount < 0n ? -amount : amount;
  return `${amount < 0n ? '-' : ''}${absolute / 100n}.${(absolute % 100n).toString().padStart(2, '0')}`;
}

function nairaInputToKobo(value: string): string {
  const match = /^\s*(-)?(\d+)(?:\.(\d{1,2}))?\s*$/.exec(value);
  if (!match) throw new Error('Enter the VAT amount in naira, for example 1250.00.');
  const kobo = BigInt(match[2]!) * 100n + BigInt((match[3] ?? '').padEnd(2, '0') || '0');
  return `${match[1] ? '-' : ''}${kobo}`;
}

function EvidenceSourceActions({ item, onUnlink, onView, onReviewOwnership, onHumanClassify, onRetry }: { item: EvidenceItem;
  onView(): void;
  onRetry?(): Promise<void> | void;
  onReviewOwnership(decision: 'confirm-selected-taxpayer' | 'flag-wrong-taxpayer'): Promise<void> | void;
  onHumanClassify(input: HumanEvidenceClassificationInput): Promise<void> | void;
  onUnlink(input: { reason: EvidenceDispositionCode; note?: string }): Promise<void> | void }) {
  const ios = useIOSPresentation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<EvidenceDispositionCode>('wrong-transaction');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string>();
  const [classificationOpen, setClassificationOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelPosition, setPanelPosition] = useState<{ left: number; maxHeight: number; top: number; width: number }>();
  useLayoutEffect(() => {
    if (!open) { setPanelPosition(undefined); return; }
    const positionPanel = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const edge = 16; const gap = 8;
      const anchor = trigger.getBoundingClientRect();
      const width = Math.min(352, Math.max(240, window.innerWidth - edge * 2));
      const availableBelow = Math.max(0, window.innerHeight - anchor.bottom - gap - edge);
      const availableAbove = Math.max(0, anchor.top - gap - edge);
      const measuredHeight = panelRef.current?.scrollHeight ?? 320;
      const placeAbove = availableBelow < Math.min(measuredHeight, 240) && availableAbove > availableBelow;
      const maxHeight = Math.max(120, placeAbove ? availableAbove : availableBelow);
      const renderedHeight = Math.min(measuredHeight, maxHeight);
      setPanelPosition({
        left: Math.max(edge, Math.min(anchor.right - width, window.innerWidth - width - edge)),
        maxHeight,
        top: placeAbove ? Math.max(edge, anchor.top - gap - renderedHeight) : anchor.bottom + gap,
        width,
      });
    };
    positionPanel();
    const frame = window.requestAnimationFrame(positionPanel);
    window.addEventListener('resize', positionPanel);
    document.addEventListener('scroll', positionPanel, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', positionPanel);
      document.removeEventListener('scroll', positionPanel, true);
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') { setOpen(false); triggerRef.current?.focus(); } };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [open]);
  const resolveOwnership = async (decision: 'confirm-selected-taxpayer' | 'flag-wrong-taxpayer') => {
    setBusy(true); setActionError(undefined);
    try { await onReviewOwnership(decision); }
    catch (error) { setActionError(error instanceof Error ? error.message : 'The ownership decision could not be recorded.'); }
    finally { setBusy(false); }
  };
  const unlink = async () => {
    setBusy(true); setActionError(undefined);
    try { await onUnlink({ reason, ...(note.trim() ? { note: note.trim() } : {}) }); setOpen(false); }
    catch (error) { setActionError(error instanceof Error ? error.message : 'The source could not be permanently deleted.'); }
    finally { setBusy(false); }
  };
  if (item.state === 'unlinked' || item.state === 'superseded') {
    return <div className="am-evidence-disposition"><strong>{item.disposition?.code.replaceAll('-', ' ') ?? item.state}</strong>
      {item.disposition?.note && <small>{item.disposition.note}</small>}<Button disabled={busy} loading={busy}
        onClick={() => void unlink()} size="small" tone="danger">Delete permanently</Button></div>;
  }
  return <div className="am-evidence-source-actions"><Cluster>{item.documentAvailable && <Button onClick={onView} size="small">View document</Button>}
    {item.documentAvailable && item.serverEvidenceId && ['blocked', 'processing-failed'].includes(item.state)
      && <Button onClick={() => setClassificationOpen(true)} size="small">Review and classify</Button>}
    {item.processingRetryable !== false && ['processing-failed', 'processing-delayed'].includes(item.state) && onRetry && <Button disabled={busy} loading={busy} onClick={async () => {
      setBusy(true); setActionError(undefined);
      try { await onRetry(); } catch (error) {
        setActionError(error instanceof Error ? error.message : 'Processing could not be resumed.');
      } finally { setBusy(false); }
    }} size="small">Resume processing</Button>}
    <Button aria-controls={`am-source-actions-${item.id}`} aria-expanded={open} onClick={() => setOpen((value) => !value)}
      ref={triggerRef} size="small" tone="secondary">{ios ? 'Delete evidence' : 'Delete document'}</Button></Cluster>
    {!item.duplicateOfEvidenceId && Boolean(item.serverOwnershipProposals?.length)
      && (item.ownershipState === 'mismatch' || item.ownershipState === 'unverified')
      && <div className="am-evidence-ownership-actions" role="group" aria-label={`Resolve ownership for ${item.name}`}>
      <strong>Choose who this document belongs to</strong>
      <small>Review the source first. This choice controls whether its proposals may be considered for the selected tax record.</small>
      <Button disabled={busy} loading={busy} onClick={() => void resolveOwnership('confirm-selected-taxpayer')} size="small">Use for {item.taxpayerName ?? 'selected taxpayer'}</Button>
      <Button disabled={busy} onClick={() => void resolveOwnership('flag-wrong-taxpayer')} size="small" tone="secondary">Remove as wrong taxpayer</Button>
    </div>}
    {actionError && <small className="am-review-decision-error" role="alert">{actionError}</small>}
    {open && createPortal(<div aria-label={`Delete ${item.name} permanently`} className="am-evidence-source-actions__panel"
      id={`am-source-actions-${item.id}`} ref={panelRef} role="group" style={panelPosition ? {
        left: panelPosition.left, maxHeight: panelPosition.maxHeight, top: panelPosition.top, width: panelPosition.width,
      } : { visibility: 'hidden' }}>
      <strong>{ios ? 'Delete this evidence permanently' : 'Delete this document permanently'}</strong>
      <label><span>Why should this source be deleted?</span><select value={reason} onChange={(event) => setReason(event.target.value as EvidenceDispositionCode)}>
        <option value="wrong-transaction">Wrong transaction</option><option value="wrong-taxpayer">Wrong taxpayer</option>
        <option value="wrong-period">Wrong filing period</option><option value="unrelated">Not relevant</option>
        <option value="duplicate">Duplicate source</option>
      </select></label>
      <label><span>Note (optional)</span><textarea onChange={(event) => setNote(event.target.value)} rows={2} value={note} /></label>
      <p>This deletes the stored file, removes its proposals from active and recoverable use, and invalidates dependent tax work. It cannot be undone. Non-viewable audit and provenance records remain, but they cannot restore the document.</p>
      <Cluster><Button disabled={busy} loading={busy} onClick={() => void unlink()} size="small" tone="danger">Delete permanently</Button>
        <Button onClick={() => setOpen(false)} size="small" tone="secondary">Cancel</Button></Cluster>
    </div>, document.body)}
    {classificationOpen && <HumanEvidenceClassificationDialog item={item} onCancel={() => setClassificationOpen(false)}
      onView={onView} onConfirm={async (input) => {
        setBusy(true); setActionError(undefined);
        try { await onHumanClassify(input); setClassificationOpen(false); }
        catch (error) { setActionError(error instanceof Error ? error.message : 'The human classification could not be recorded.'); }
        finally { setBusy(false); }
      }} busy={busy} />}
  </div>;
}

function HumanEvidenceClassificationDialog({ item, busy, onCancel, onConfirm, onView }: {
  item: EvidenceItem; busy: boolean; onCancel(): void; onView(): void;
  onConfirm(input: HumanEvidenceClassificationInput): Promise<void> | void;
}) {
  const ios = useIOSPresentation();
  const [category, setCategory] = useState<HumanEvidenceClassificationInput['evidenceCategory'] | ''>(
    EVIDENCE_CATEGORIES.includes(item.classificationCategory as typeof EVIDENCE_CATEGORIES[number])
      ? item.classificationCategory as HumanEvidenceClassificationInput['evidenceCategory'] : '');
  const [reviewed, setReviewed] = useState(false);
  const [notes, setNotes] = useState('');
  const [validationError, setValidationError] = useState<string>();
  const reasonCode: HumanEvidenceClassificationInput['reasonCode'] = item.duplicateOfEvidenceId
    ? 'DUPLICATE_SOURCE_DISTINCT_USE' : item.state === 'processing-failed'
      ? 'EXTRACTION_UNAVAILABLE' : 'AUTOMATED_CLASSIFICATION_DISAGREED';
  return createPortal(<div className="am-document-dialog__backdrop"><section aria-labelledby={`human-classification-${item.id}`}
    aria-modal="true" className="am-evidence-human-classification" role="dialog">
    <header><div><p className="am-eyebrow">{ios ? 'Human evidence decision' : 'Your document review'}</p>
      <h2 id={`human-classification-${item.id}`}>Classify {item.name}</h2>
      <p>Amoye’s assessment is a proposal. Your recorded review decides how this source is linked to the selected tax record.</p></div>
      <button aria-label="Close classification review" className="am-document-dialog__close" disabled={busy}
        onClick={onCancel} type="button"><Icon name="close" size={20} /></button></header>
    <div className="am-evidence-human-classification__body">
      <div className="am-evidence-human-classification__warning"><strong>Current automated assessment</strong>
        <p>{item.issue ?? (ios ? 'No reliable evidence category was proposed.' : 'No reliable document category was proposed.')}</p></div>
      <Button disabled={busy} onClick={onView} size="small" tone="secondary">View secured document</Button>
      <label><span>Use this document as</span><select disabled={busy} value={category}
        onChange={(event) => setCategory(event.target.value as HumanEvidenceClassificationInput['evidenceCategory'])}>
        <option value="">Choose a filing category</option>{EVIDENCE_CATEGORIES.map((value) => <option key={value} value={value}>
          {EVIDENCE_CATEGORY_LABELS[value]}</option>)}</select></label>
      <label><span>Review note (optional)</span><textarea disabled={busy} maxLength={2_000}
        onChange={(event) => setNotes(event.target.value)} rows={3} value={notes} /></label>
      <label className="am-evidence-human-classification__attestation"><input checked={reviewed} disabled={busy}
        onChange={(event) => setReviewed(event.target.checked)} type="checkbox" />
        <span>I reviewed the secured source and confirm that it belongs to {item.taxpayerName ?? 'the selected taxpayer'}. I understand that Amoye’s OCR and classification are not authoritative.</span></label>
      <p>This records your decision and its audit trail. It does not create transaction amounts that were not extracted or reviewed.</p>
      {validationError && <small className="am-review-decision-error" role="alert">{validationError}</small>}
    </div>
    <footer><Cluster><Button disabled={busy || !category || !reviewed} loading={busy} onClick={() => {
      if (!category) { setValidationError('Choose a filing category.'); return; }
      if (!reviewed) { setValidationError('Confirm that you reviewed the source.'); return; }
      setValidationError(undefined); void onConfirm({ evidenceCategory: category, reasonCode,
        ...(notes.trim() ? { notes: notes.trim() } : {}) });
    }}>Record my classification</Button><Button disabled={busy} onClick={onCancel} tone="secondary">Cancel</Button></Cluster></footer>
  </section></div>, document.body);
}

function EvidenceDocumentViewer({ item, load, onClose }: { item: EvidenceItem; load(): Promise<EvidenceDocumentView>; onClose(): void }) {
  const ios = useIOSPresentation();
  const [sourceDocument, setSourceDocument] = useState<EvidenceDocumentView>();
  const [objectUrl, setObjectUrl] = useState<string>();
  const [textPreview, setTextPreview] = useState<string>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    let active = true; let url: string | undefined;
    void load().then(async (value) => {
      if (!active) return;
      setSourceDocument(value);
      if (value.mimeType === 'text/csv' || value.mimeType === 'application/json') {
        setTextPreview((await value.blob.text()).slice(0, 500_000));
      }
      if (typeof URL.createObjectURL === 'function') {
        url = URL.createObjectURL(value.blob); setObjectUrl(url);
      }
    }).catch((cause) => { if (active) setError(cause instanceof Error ? cause.message : 'The document could not be opened.'); });
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [load]);
  const previewableImage = sourceDocument?.mimeType.startsWith('image/');
  return createPortal(<div className="am-document-dialog__backdrop"><section aria-label={`Review ${item.name}`} aria-modal="true"
    className="am-document-dialog am-evidence-viewer" role="dialog"><header className="am-document-dialog__header"><div>
      <p className="am-eyebrow">{ios ? 'Evidence review' : 'Document review'}</p><h2>{item.name}</h2><p>Bound to {item.taxpayerName ?? 'the selected taxpayer'} · {item.hashLabel}</p></div>
      <button aria-label={ios ? 'Close evidence viewer' : 'Close document viewer'} className="am-document-dialog__close" onClick={onClose} type="button"><Icon name="close" size={20} /></button>
    </header><div className="am-document-dialog__body am-evidence-viewer__body">
      {!sourceDocument && !error && <p aria-live="polite" className="am-document-progress">Opening the secured source…</p>}
      {error && <p className="am-document-error" role="alert">{error}</p>}
      {textPreview !== undefined && <pre aria-label="Document text">{textPreview}</pre>}
      {previewableImage && objectUrl && <img alt={`${ios ? 'Original evidence' : 'Original source document'}: ${item.name}`} src={objectUrl} />}
      {sourceDocument?.mimeType === 'application/pdf' && objectUrl && <iframe sandbox="" src={objectUrl} title={`${ios ? 'Original evidence' : 'Original source document'}: ${item.name}`} />}
      {sourceDocument && textPreview === undefined && !previewableImage && sourceDocument.mimeType !== 'application/pdf' && <EmptyState compact
        title="Preview is not available for this file type" description="The original secured file can still be opened or downloaded for review." />}
      {item.ownershipProposal && <aside className="am-evidence-ownership-proposal"><strong>Source ownership proposal</strong>
        <span>{item.ownershipProposal.detectedName ?? 'No labelled owner name detected'}</span>
        {item.ownershipProposal.taxIdentifier && <span>Tax ID: {item.ownershipProposal.taxIdentifier}</span>}
        <small>{item.ownershipProposal.confidence ? `Confidence ${item.ownershipProposal.confidence} · ` : ''}{item.ownershipProposal.extractor}</small></aside>}
    </div><footer className="am-document-dialog__footer"><span>Viewing does not accept source-analysis output or change the tax record.</span><Cluster>
      {objectUrl && sourceDocument && <a className="am-button am-button--secondary am-button--small" download={sourceDocument.filename} href={objectUrl}>Download original</a>}
      <Button onClick={onClose}>Close</Button></Cluster></footer></section></div>, globalThis.document.body);
}

type WorkspaceNavigationProps<T extends string> = {
  active: T; items: readonly WorkspaceNavigationItem<T>[]; label?: string;
  mobileSettings?: { active: boolean; onOpen(): void }; onChange(view: T): void;
};
export function WorkspaceNavigation<T extends string>(props: WorkspaceNavigationProps<T>) {
  const ios = useIOSPresentation();
  return ios ? <IOSNavigation {...props} /> : <StandardWorkspaceNavigation {...props} />;
}

function StandardWorkspaceNavigation<T extends string>({ active, items, label = 'Workspace sections', mobileSettings, onChange }: {
  active: T;
  items: readonly WorkspaceNavigationItem<T>[];
  label?: string;
  mobileSettings?: { active: boolean; onOpen(): void };
  onChange: (item: T) => void;
}) {
  const account = useWorkspaceAccount();
  const chromeHosts = useWorkspaceChromeHosts();
  const standardMenu = useStandardMenuContext();
  const phoneNavigation = usePhoneNavigation();
  const [tooltip, setTooltip] = useState<{ id: string; label: string; left: number; top: number }>();
  const [localMobileMenuRequested, setLocalMobileMenuRequested] = useState(false);
  const [mobileMenuRendered, setMobileMenuRendered] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const mobileMenuCloseTimerRef = useRef<number | undefined>(undefined);
  const mobileMenuRestoreFocusRef = useRef(false);
  const mobileSwipeStartRef = useRef<{ x: number; y: number } | undefined>(undefined);
  const mobileMenuRequested = chromeHosts ? chromeHosts.activeMobileLayer === 'menu' : localMobileMenuRequested;
  useEffect(() => mobileSettings && chromeHosts?.registerSettings?.(mobileSettings.onOpen),
    [chromeHosts?.registerSettings, mobileSettings?.onOpen]);
  const [locallyCollapsed, setCollapsed] = useState(() => {
    try { return window.localStorage.getItem('amoye-workspace-navigation-collapsed') === 'true'; }
    catch { return false; }
  });
  const collapsed = standardMenu?.active ? standardMenu.collapsed : locallyCollapsed;
  const toggle = () => setCollapsed((current) => {
    const next = !current;
    try { window.localStorage.setItem('amoye-workspace-navigation-collapsed', String(next)); } catch { /* preference is optional */ }
    return next;
  });
  const showTooltip = (element: HTMLElement, item: WorkspaceNavigationItem<T>) => {
    if (!collapsed) return;
    const bounds = element.getBoundingClientRect();
    setTooltip({ id: `am-navigation-tooltip-${item.id}`, label: `${item.label} — ${item.description}`,
      left: bounds.right + 10, top: bounds.top + bounds.height / 2 });
  };
  const hideTooltip = () => setTooltip(undefined);
  const activeMobileItem = mobileSettings?.active
    ? { id: 'settings', label: 'Settings', description: 'Account and preferences', icon: 'settings' as const }
    : items.find((item) => item.id === active) ?? items[0];
  const openMobileMenu = () => {
    mobileMenuRestoreFocusRef.current = false;
    if (chromeHosts) chromeHosts.openMobileLayer('menu');
    else setLocalMobileMenuRequested(true);
  };
  const closeMobileMenu = (restoreFocus = true) => {
    mobileMenuRestoreFocusRef.current = restoreFocus;
    if (chromeHosts) chromeHosts.closeMobileLayer('menu');
    else setLocalMobileMenuRequested(false);
  };
  useEffect(() => {
    if (mobileMenuCloseTimerRef.current) window.clearTimeout(mobileMenuCloseTimerRef.current);
    if (mobileMenuRequested) {
      setMobileMenuRendered(true);
      const frame = window.requestAnimationFrame(() => setMobileMenuVisible(true));
      return () => window.cancelAnimationFrame(frame);
    }
    setMobileMenuVisible(false);
    if (!mobileMenuRendered) return;
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    mobileMenuCloseTimerRef.current = window.setTimeout(() => {
      setMobileMenuRendered(false);
      if (mobileMenuRestoreFocusRef.current) window.requestAnimationFrame(() => mobileMenuButtonRef.current?.focus());
      mobileMenuRestoreFocusRef.current = false;
    }, reducedMotion ? 0 : 220);
  }, [mobileMenuRendered, mobileMenuRequested]);
  useEffect(() => {
    if (mobileMenuRendered) closeMobileMenu(false);
  }, [active]);
  useEffect(() => {
    if (!mobileMenuRendered) return;
    document.documentElement.classList.add('am-mobile-navigation-is-open');
    if (mobileMenuVisible) mobileMenuButtonRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); closeMobileMenu(); }
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.documentElement.classList.remove('am-mobile-navigation-is-open');
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [mobileMenuRendered, mobileMenuVisible]);
  useEffect(() => {
    if (phoneNavigation || !mobileMenuRendered) return;
    closeMobileMenu(false);
  }, [phoneNavigation, mobileMenuRendered]);
  useEffect(() => () => {
    if (mobileMenuCloseTimerRef.current) window.clearTimeout(mobileMenuCloseTimerRef.current);
  }, []);

  if (phoneNavigation) {
    const trigger = <button aria-expanded={mobileMenuVisible}
      aria-label={mobileMenuRendered ? 'Close workspace menu' : `Workspace sections. Current section: ${activeMobileItem?.label ?? 'None'}`}
      className="account-header__mobile-action account-header__mobile-menu-button"
      onClick={mobileMenuRendered ? () => closeMobileMenu() : openMobileMenu}
      ref={mobileMenuButtonRef} title={mobileMenuRendered ? 'Close menu' : 'Open menu'} type="button">
      <span className="am-mobile-navigation__icon"><Icon name={mobileMenuRendered ? 'chevron-left' : 'menu'} size={20} /></span>
    </button>;
    return <nav aria-label={label} className="am-mobile-navigation">
    {mobileMenuRendered && typeof document !== 'undefined' ? createPortal(trigger, document.body)
      : chromeHosts?.mobileMenu ? createPortal(trigger, chromeHosts.mobileMenu) : trigger}
    <div aria-live="polite" className="am-mobile-navigation__current-page">{activeMobileItem?.label ?? 'Workspace'}</div>
    {mobileMenuRendered && <div className={`am-mobile-navigation__layer${mobileMenuVisible ? ' is-open' : ''}`}>
      <button aria-label="Dismiss workspace menu" className="am-mobile-navigation__backdrop"
        onClick={() => closeMobileMenu()} type="button" />
      <section aria-label="Workspace sections" aria-modal="true" className="am-mobile-navigation__sheet"
        onPointerDown={(event) => { mobileSwipeStartRef.current = { x: event.clientX, y: event.clientY }; }}
        onPointerUp={(event) => {
          const start = mobileSwipeStartRef.current; mobileSwipeStartRef.current = undefined;
          if (!start) return;
          const horizontal = event.clientX - start.x; const vertical = event.clientY - start.y;
          if (horizontal < -72 && Math.abs(horizontal) > Math.abs(vertical) * 1.2) closeMobileMenu();
        }} role="dialog">
        <header><div><span>Workspace</span><strong>Sections</strong></div></header>
        {items.length > 0 && <div className="am-mobile-navigation__secondary">{items.map((item) => <button
          aria-current={!mobileSettings?.active && item.id === active ? 'page' : undefined}
          aria-label={`${item.label}. ${item.description}${item.count !== undefined && item.count > 0 ? `. ${item.count} items` : ''}`}
          key={item.id} onClick={() => { onChange(item.id); closeMobileMenu(false); }} type="button">
          <span className="am-workspace-navigation__icon"><Icon name={item.icon ?? 'file-text'} size={20} /></span>
          <span><strong>{item.label}</strong><small>{item.description}</small></span>
          {item.count !== undefined && item.count > 0 && <b aria-label={`${item.count} items`}>{item.count}</b>}
        </button>)}{mobileSettings && <button aria-current={mobileSettings.active ? 'page' : undefined}
          aria-label="Settings. Account, security and Amoye preferences" onClick={() => {
            mobileSettings.onOpen(); closeMobileMenu(false);
          }} type="button"><span className="am-workspace-navigation__icon"><Icon name="settings" size={20} /></span>
          <span><strong>Settings</strong><small>Account, security and Amoye preferences</small></span></button>}</div>}
        {account && !account.identityInHeader && <footer><div aria-label={`Signed in as ${account.displayName}, ${account.capacity}`}
          className="am-mobile-navigation__identity" title={account.email}>
          <span aria-hidden="true" className="am-workspace-navigation__avatar">{accountInitials(account.displayName)}</span>
          <span><strong>{account.displayName}</strong><small>{account.capacity}</small></span>
        </div><button aria-label="Sign out" className="am-mobile-navigation__sign-out" onClick={account.onSignOut}
          type="button"><Icon name="log-out" size={19} /><span>Sign out</span></button></footer>}
      </section>
    </div>}
  </nav>;
  }

  const desktopNavigation = <nav aria-label={label} className={`am-workspace-navigation${collapsed ? ' am-workspace-navigation--collapsed' : ''}`}>
    {!standardMenu?.active && <div className="am-workspace-navigation__heading"><span>Workspace</span><button aria-expanded={!collapsed}
      aria-label={collapsed ? 'Expand workspace navigation' : 'Collapse workspace navigation'} className="am-workspace-navigation__toggle"
      onClick={toggle} title={collapsed ? 'Expand navigation' : 'Collapse navigation'} type="button">
      <Icon name={collapsed ? 'chevron-right' : 'chevron-left'} size={17} />
    </button></div>}
    <div className="am-workspace-navigation__items">{items.map((item) => <button
      aria-describedby={tooltip?.id === `am-navigation-tooltip-${item.id}` ? tooltip.id : undefined}
      aria-current={!mobileSettings?.active && item.id === active ? 'page' : undefined} aria-label={`${item.label}. ${item.description}${item.count !== undefined && item.count > 0 ? `. ${item.count} items` : ''}`}
      className="am-workspace-navigation__item" key={item.id} onBlur={hideTooltip}
      onClick={() => onChange(item.id)} onFocus={(event) => showTooltip(event.currentTarget, item)}
      onPointerEnter={(event) => showTooltip(event.currentTarget, item)} onPointerLeave={hideTooltip}
      title={collapsed ? `${item.label} — ${item.description}` : undefined} type="button">
      <span className="am-workspace-navigation__icon"><Icon name={item.icon ?? 'file-text'} size={19} /></span>
      <span className="am-workspace-navigation__copy"><span>{item.label}</span><small>{item.description}</small></span>
      {item.count !== undefined && item.count > 0 && <strong aria-label={`${item.count} items`}>{item.count}</strong>}
    </button>)}{mobileSettings && <button aria-current={mobileSettings.active ? 'page' : undefined}
      aria-label="Settings. Account, security and Amoye preferences" className="am-workspace-navigation__item am-workspace-navigation__settings"
      onClick={mobileSettings.onOpen} title={collapsed ? 'Settings — Account, security and Amoye preferences' : undefined}
      type="button"><span className="am-workspace-navigation__icon"><Icon name="settings" size={19} /></span>
      <span className="am-workspace-navigation__copy"><span>Settings</span><small>Account, security and preferences</small></span>
    </button>}</div>
    {account && !account.identityInHeader && <div className="am-workspace-navigation__account">
      <div aria-label={`Signed in as ${account.displayName}, ${account.capacity}`} className="am-workspace-navigation__identity"
        title={account.email}>
        <span aria-hidden="true" className="am-workspace-navigation__avatar">{accountInitials(account.displayName)}</span>
        <span className="am-workspace-navigation__account-copy"><strong>{account.displayName}</strong><small>{account.capacity}</small></span>
      </div>
      <button aria-label="Sign out" className="am-workspace-navigation__sign-out" onClick={account.onSignOut}
        title="Sign out" type="button"><Icon name="log-out" size={18} /><span>Sign out</span></button>
    </div>}
    {tooltip && createPortal(<div className="am-workspace-navigation-tooltip" id={tooltip.id} role="tooltip"
      style={{ left: tooltip.left, top: tooltip.top }}>{tooltip.label}</div>, globalThis.document.body)}
  </nav>;
  return standardMenu?.active && standardMenu.host ? createPortal(desktopNavigation, standardMenu.host) : desktopNavigation;
}

function usePhoneNavigation(): boolean {
  const query = '(max-width: 42rem)';
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(query).matches : false);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  return matches;
}

export function CalculationSummary({ snapshot }: { snapshot: CalculationSnapshot }) {
  return <Panel id="calculation-trace" tabIndex={-1} title="Tax estimate" description={`Calculated from reviewed figures using the tax rules effective ${snapshot.effectiveDate}. Optional assistance can explain this estimate but cannot change it.`}>
    <MetricGrid><MetricCard detail="Based on the latest reviewed figures" label="Calculation status" value={`${snapshot.version} · ${snapshot.status.replaceAll('-', ' ')}`} /><MetricCard detail="An estimate until the required reviews and filing connection are complete" label="Estimated position" value={snapshot.netPositionLabel} /></MetricGrid>
    <DataTable caption="How this estimate was calculated" columns={[
      { id: 'line', header: 'Line', render: (line: CalculationSnapshot['lines'][number]) => line.label },
      { id: 'amount', header: 'Amount', align: 'end', render: (line: CalculationSnapshot['lines'][number]) => line.amountLabel },
      { id: 'basis', header: 'How it was calculated', render: (line: CalculationSnapshot['lines'][number]) => line.basis },
    ]} getRowKey={(line) => line.id} rows={snapshot.lines} />
  </Panel>;
}

export function ResponsibilityHandoff({ onChooseOptionalActor, ports, steps }: { onChooseOptionalActor?: () => void;
  ports: Pick<SharedJourneyPorts, 'performResponsibilityAction'>; steps: readonly ResponsibilityStep[] }) {
  return <Panel id="filing-review" tabIndex={-1} title="Filing steps" description="See who needs to complete each step and what you can do next."><ol className="am-responsibility-list">{steps.map((step) => <li key={step.id}><div><Cluster><StatusBadge tone={toneFor(step.state)}>{step.state}</StatusBadge>
    {step.optional && <StatusBadge tone="neutral">optional</StatusBadge>}</Cluster><h3>{responsibilityActionLabel(step.action)}</h3><p>{step.actorLabel}</p>
    <p className="am-feature-muted">{step.explanation}</p></div>{step.optional && step.actorLabel.toLowerCase().startsWith('no ') && onChooseOptionalActor
      ? <Button onClick={onChooseOptionalActor} size="small" tone="secondary">Choose practitioner</Button>
      : step.state === 'ready' && <Button onClick={() => void ports.performResponsibilityAction(step.action, step.artifactVersion)} size="small">Continue</Button>}</li>)}</ol></Panel>;
}

function responsibilityActionLabel(action: ResponsibilityStep['action']): string {
  const labels: Record<ResponsibilityStep['action'], string> = {
    prepare: 'Prepare return', 'professional-review': 'Professional review', approve: 'Approve return',
    'declare-sign': 'Review and sign', 'request-mock-submission': 'File return',
  };
  return labels[action];
}

export function StandardsBridgeStatus({ bridge, ports }: { bridge: BridgeState; ports: Pick<SharedJourneyPorts, 'reconcileBridge' | 'retryBridge'> }) {
  const unavailable = bridge.mode === 'unavailable';
  return <Panel title="Filing status" description={unavailable
    ? 'Filing is not available yet. You can continue preparing and reviewing your return.'
    : bridge.mode === 'mock' ? 'Testing is available. Nothing submitted here will be filed with the tax authority.'
      : 'A filing test is available. It does not create an official filing outcome.'} tone={unavailable ? 'muted' : 'default'}>
    <Cluster><StatusBadge tone={unavailable ? 'warning' : 'info'}>{unavailable ? 'not available' : 'testing only'}</StatusBadge>
      <strong>{unavailable ? 'Your work remains saved' : 'No official filing will be created'}</strong></Cluster>
    {bridge.reconciliationRequired && <ErrorState action={<Button onClick={() => void ports.reconcileBridge()} size="small">Check filing status</Button>} compact description="Check the existing request before trying again so the same return is not sent twice." title="Filing status needs attention" />}
    {bridge.retryAllowed && <Button onClick={() => void ports.retryBridge()} tone="secondary">Try filing test again</Button>}
  </Panel>;
}

export function SubmissionTimeline({ events }: { events: readonly SubmissionEvent[] }) {
  return <Panel iosDisclosure title="Filing history" description="Follow each request here. A test receipt is not confirmation from the tax authority.">{events.length === 0 ? <EmptyState description="Filing activity will appear here after an authorised person starts a request." title="No filing request yet" /> : <ol className="am-timeline">{events.map((event) => <li className={`am-timeline--${event.state}`} key={event.id}><StatusBadge tone={toneFor(event.state)}>{event.state}</StatusBadge><div><h3>{event.label}</h3><p>{event.detail}</p><small>{event.owner}{event.occurredAt ? ` · ${event.occurredAt}` : ''}</small></div></li>)}</ol>}</Panel>;
}

export interface IntelligenceAttentionAction {
  id: string;
  title: string;
  detail: string;
  contextLabel?: string;
  priority: 'urgent' | 'high' | 'normal' | 'opportunity';
  nextLabel: string;
  onOpen(): void;
}

function IntelligenceAttentionList({ attention, onOpen }: {
  attention: readonly IntelligenceAttentionAction[];
  onOpen?(item: IntelligenceAttentionAction): void;
}) {
  return <div className="am-intelligence-overlay__attention">
    {attention.length === 0
      ? <EmptyState compact title="Nothing urgent is visible"
        description="Amoye will list explainable deadlines, exceptions and opportunities here." />
      : <ol>{attention.map((item) => <li key={item.id}><div>
        <StatusBadge tone={item.priority === 'urgent' ? 'danger' : item.priority === 'high' ? 'warning' : 'info'}>
          {item.priority}
        </StatusBadge>
        <strong>{item.title}</strong>{item.contextLabel && <small>{item.contextLabel}</small>}<p>{item.detail}</p>
      </div><Button onClick={() => { item.onOpen(); onOpen?.(item); }} size="small" tone="secondary">
        {item.nextLabel}
      </Button></li>)}</ol>}
  </div>;
}

interface IntelligenceConversationTurn {
  id: string;
  question: string;
  state: 'working' | 'complete' | 'failed';
  response?: GeneratedIntelligenceAnswer;
  error?: string;
  activity: Array<{ label: string; state: 'working' | 'complete' | 'failed' }>;
}

const intelligenceDisclaimer = 'Amoye can make mistakes, so check the sources it shows you. It can explain your records and take you to the right screen, but it cannot change tax information, calculate tax, approve evidence, sign, file, or make payments for you.';
const standardIntelligenceDisclaimer = 'Amoye can make mistakes, so check the sources it shows you. It can explain your records and take you to the right screen, but it cannot change tax information, calculate tax, approve records, sign, file, or make payments for you.';
const intelligenceHistoryDeletedEvent = 'amoye:intelligence-history-deleted';
const intelligenceDraftChangedEvent = 'amoye:intelligence-draft-changed';
const intelligenceSurfaceChangedEvent = 'amoye:intelligence-surface-changed';
const intelligenceDraftStoragePrefix = 'amoye-intelligence-draft:v1:';
const intelligenceSurfaceStoragePrefix = 'amoye-intelligence-surface:v1:';

export type IntelligenceSurfaceView = 'ask' | 'attention';

interface IntelligenceSurfaceState {
  view: IntelligenceSurfaceView;
  submitRequestId?: string;
}

function intelligenceDraftStorageKey(conversationScope: string): string {
  return `${intelligenceDraftStoragePrefix}${encodeURIComponent(conversationScope)}`;
}

function readIntelligenceDraft(conversationScope: string): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.sessionStorage.getItem(intelligenceDraftStorageKey(conversationScope)) ?? '';
  } catch {
    return '';
  }
}

function persistIntelligenceDraft(conversationScope: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    const key = intelligenceDraftStorageKey(conversationScope);
    if (value) window.sessionStorage.setItem(key, value);
    else window.sessionStorage.removeItem(key);
  } catch {
    // The in-memory draft still works when browser storage is unavailable.
  }
  window.dispatchEvent(new CustomEvent(intelligenceDraftChangedEvent, {
    detail: { conversationScope, value },
  }));
}

export function useIntelligenceDraft(conversationScope: string): readonly [string, (value: string) => void] {
  const [draft, setDraft] = useState(() => readIntelligenceDraft(conversationScope));
  useEffect(() => setDraft(readIntelligenceDraft(conversationScope)), [conversationScope]);
  useEffect(() => {
    const synchronize = (event: Event) => {
      const detail = (event as CustomEvent<{ conversationScope?: string; value?: string }>).detail;
      if (detail?.conversationScope === conversationScope && typeof detail.value === 'string') setDraft(detail.value);
    };
    window.addEventListener(intelligenceDraftChangedEvent, synchronize);
    return () => window.removeEventListener(intelligenceDraftChangedEvent, synchronize);
  }, [conversationScope]);
  const updateDraft = (value: string) => {
    setDraft(value);
    persistIntelligenceDraft(conversationScope, value);
  };
  return [draft, updateDraft] as const;
}

function intelligenceSurfaceStorageKey(conversationScope: string): string {
  return `${intelligenceSurfaceStoragePrefix}${encodeURIComponent(conversationScope)}`;
}

function readIntelligenceSurface(conversationScope: string): IntelligenceSurfaceState {
  if (typeof window === 'undefined') return { view: 'ask' };
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(intelligenceSurfaceStorageKey(conversationScope)) ?? '{}') as
      Partial<IntelligenceSurfaceState>;
    return { view: stored.view === 'attention' ? 'attention' : 'ask',
      ...(typeof stored.submitRequestId === 'string' ? { submitRequestId: stored.submitRequestId } : {}) };
  } catch {
    return { view: 'ask' };
  }
}

function persistIntelligenceSurface(conversationScope: string, state: IntelligenceSurfaceState): void {
  if (typeof window === 'undefined') return;
  try { window.sessionStorage.setItem(intelligenceSurfaceStorageKey(conversationScope), JSON.stringify(state)); }
  catch { /* The event still synchronizes mounted surfaces when storage is unavailable. */ }
  window.dispatchEvent(new CustomEvent(intelligenceSurfaceChangedEvent, { detail: { conversationScope, state } }));
}

/** Selects the same Ask/Attention view across Home, the docked assistant and
 * the full Intelligence page. A submit request is consumed once by the full
 * page, so a Home question cannot be sent twice after navigation. */
export function requestIntelligenceSurface(conversationScope: string, view: IntelligenceSurfaceView,
  options: { submit?: boolean } = {}): void {
  const current = readIntelligenceSurface(conversationScope);
  persistIntelligenceSurface(conversationScope, { view,
    ...(options.submit ? { submitRequestId: crypto.randomUUID() }
      : current.submitRequestId ? { submitRequestId: current.submitRequestId } : {}) });
}

/** Carries an unsent question into a deliberately different full-page scope,
 * such as moving from one selected practice client to the portfolio page. */
export function carryIntelligenceDraft(fromScope: string, toScope: string,
  view: IntelligenceSurfaceView): void {
  const draft = readIntelligenceDraft(fromScope);
  if (draft) persistIntelligenceDraft(toScope, draft);
  requestIntelligenceSurface(toScope, view);
}

function consumeIntelligenceSubmit(conversationScope: string, submitRequestId: string): void {
  const current = readIntelligenceSurface(conversationScope);
  if (current.submitRequestId !== submitRequestId) return;
  persistIntelligenceSurface(conversationScope, { view: current.view });
}

function useIntelligenceSurface(conversationScope: string): readonly [IntelligenceSurfaceState,
  (view: IntelligenceSurfaceView) => void] {
  const [state, setState] = useState(() => readIntelligenceSurface(conversationScope));
  useEffect(() => setState(readIntelligenceSurface(conversationScope)), [conversationScope]);
  useEffect(() => {
    const synchronize = (event: Event) => {
      const detail = (event as CustomEvent<{ conversationScope?: string; state?: IntelligenceSurfaceState }>).detail;
      if (detail?.conversationScope === conversationScope && detail.state) setState(detail.state);
    };
    window.addEventListener(intelligenceSurfaceChangedEvent, synchronize);
    return () => window.removeEventListener(intelligenceSurfaceChangedEvent, synchronize);
  }, [conversationScope]);
  return [state, (view) => requestIntelligenceSurface(conversationScope, view)] as const;
}

function completedIntelligenceActivity(response: GeneratedIntelligenceAnswer,
  contextKind: 'CLIENT' | 'PORTFOLIO'): IntelligenceConversationTurn['activity'] {
  return response.activities?.map((activity) => ({
    label: presentAssistantActivity(activity, contextKind), state: 'complete' as const,
  })) ?? [
    { label: contextKind === 'PORTFOLIO' ? 'Confirmed I can use the practice summary available to you'
      : 'Confirmed I can use records from this tax profile', state: 'complete' },
    { label: 'Checked the relevant records', state: 'complete' },
    { label: response.citations.length > 0
      ? `Included ${response.citations.length} ${response.citations.length === 1 ? 'source' : 'sources'} you can check`
      : 'No source was available for this answer', state: 'complete' },
  ];
}

function savedIntelligenceTurns(history: Awaited<ReturnType<NonNullable<SharedJourneyPorts['loadIntelligenceHistory']>>>,
  contextKind: 'CLIENT' | 'PORTFOLIO'): IntelligenceConversationTurn[] {
  return history.map((turn) => ({ id: turn.response.generatedAt + String(turn.sequence),
    question: turn.question, state: 'complete', response: turn.response,
    activity: turn.response.activities?.map((activity) => ({
      label: presentAssistantActivity(activity, contextKind), state: 'complete' as const })) ?? [] }));
}

function publishIntelligenceHistoryDeletion(conversationScope: string): void {
  window.dispatchEvent(new CustomEvent(intelligenceHistoryDeletedEvent, { detail: { conversationScope } }));
}

function useIntelligenceHistoryDeletion(conversationScope: string,
  clearTurns: () => void): void {
  useEffect(() => {
    const clear = (event: Event) => {
      const detail = (event as CustomEvent<{ conversationScope?: string }>).detail;
      if (detail?.conversationScope === conversationScope) clearTurns();
    };
    window.addEventListener(intelligenceHistoryDeletedEvent, clear);
    return () => window.removeEventListener(intelligenceHistoryDeletedEvent, clear);
  }, [conversationScope, clearTurns]);
}

function IntelligenceTurnFeed({ conversationEndRef, onNavigate, turns }: {
  conversationEndRef: RefObject<HTMLDivElement | null>;
  onNavigate?: (intent: IntelligenceNavigationIntent) => Promise<boolean | void> | boolean | void;
  turns: readonly IntelligenceConversationTurn[];
}) {
  return <div aria-live="polite" className="am-intelligence-overlay__turns">
    {turns.map((turn) => <article className="am-intelligence-turn" key={turn.id}>
      <p className="am-intelligence-turn__question">{turn.question}</p>
      <details className="am-intelligence-turn__activity" open={turn.state === 'working'}>
        <summary>{turn.state === 'working' ? 'Checking your records…' : turn.state === 'failed' ? 'Could not finish' : `${turn.activity.length} checks completed`}</summary>
        <ol>{turn.activity.map((activity, index) => <li className={`is-${activity.state}`} key={`${activity.label}:${index}`}>
          <span aria-hidden="true" />{activity.label}</li>)}</ol>
      </details>
      {turn.response && <div className="am-intelligence-turn__answer"><AssistantAnswerContent answer={turn.response.answer} />
        {turn.response.navigationIntents?.map((intent) => <Button key={intent.id}
          onClick={() => void onNavigate?.(intent)} size="small" tone="secondary">{intent.label}</Button>)}
        {turn.response.citations.length > 0 && <details><summary>{turn.response.citations.length} source{turn.response.citations.length === 1 ? '' : 's'} checked</summary>
          <ul aria-label="Sources used by Amoye">{turn.response.citations.map((citation) => {
            const presented = presentCitation(citation);
            return <li key={`${citation.sourceId}:${citation.sourceVersion}:${citation.locator}`}>
              <strong>{presented.href ? <a href={presented.href} rel="noreferrer" target="_blank">{presented.title}</a>
                : presented.title}</strong><small>{presented.detail}</small></li>;
          })}</ul></details>}
        <small>{presentAssistantMethod(turn.response.modelId, turn.response.modelVersion).label}: {' '}
          {presentAssistantMethod(turn.response.modelId, turn.response.modelVersion).value}. {' '}
          <a href={askAmoyeDataUseHref}>How Ask Amoye uses information</a></small>
        {turn.response.limitations.length > 0 && <small>{presentAssistantLimitations(turn.response.limitations).join(' ')}</small>}</div>}
      {turn.error && <p className="am-document-error" role="alert">{turn.error}</p>}
    </article>)}
    <div ref={conversationEndRef} />
  </div>;
}

function IntelligenceComposer({ canSend, disabled, emptyPlaceholder, followUpPlaceholder, onPromptChange, onSend,
  pending, prompt, rows = 1, turnsPresent, variant = 'overlay' }: {
  canSend: boolean;
  disabled: boolean;
  emptyPlaceholder?: string;
  followUpPlaceholder?: string;
  onPromptChange(value: string): void;
  onSend(): void;
  pending: boolean;
  prompt: string;
  rows?: 1 | 2;
  turnsPresent: boolean;
  variant?: 'overlay' | 'page';
}) {
  const promptId = useId();
  const promptInputRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!prompt && promptInputRef.current) promptInputRef.current.style.height = '';
  }, [prompt]);
  const sendHint = disabled ? 'Intelligence is temporarily unavailable' : !prompt.trim() ? 'Enter a question to send'
    : pending ? 'Wait for the current answer' : 'Send question';
  return <form className={`am-intelligence-composer am-intelligence-composer--${variant}`}
    onSubmit={(event) => { event.preventDefault(); onSend(); }}>
    <label className="am-visually-hidden" htmlFor={promptId}>{turnsPresent ? 'Ask a follow-up question' : 'Ask Amoye a question'}</label>
    <div className="am-intelligence-composer__input">
      <textarea disabled={disabled || pending} id={promptId} maxLength={2000}
        onChange={(event) => {
          onPromptChange(event.target.value);
          event.currentTarget.style.height = 'auto';
          event.currentTarget.style.height = `${event.currentTarget.scrollHeight}px`;
        }} placeholder={turnsPresent ? followUpPlaceholder ?? 'Ask a follow-up question…'
          : emptyPlaceholder ?? 'Ask about your taxes'}
        ref={promptInputRef} rows={rows} value={prompt}
        onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault(); if (canSend) onSend();
        } }} />
      <div className="am-intelligence-composer__controls">
        <button aria-label={sendHint} className="am-intelligence-composer__send am-intelligence-tooltip" data-tooltip={sendHint}
          disabled={!canSend} type="submit"><Icon name="arrow-up" size={18} /></button>
      </div>
    </div>
  </form>;
}

export function IntelligencePanel({ attention = [], conversationScope = 'intelligence-page', contextKind = 'CLIENT', intelligence,
  ports }: { attention?: readonly IntelligenceAttentionAction[]; conversationScope?: string;
  contextKind?: 'CLIENT' | 'PORTFOLIO'; intelligence: IntelligenceState;
  ports: Pick<SharedJourneyPorts, 'setIntelligenceEnabled' | 'queryIntelligence'
    | 'loadIntelligenceHistory' | 'openIntelligenceIntent'> }) {
  const ios = useIOSPresentation();
  const [enabled, setEnabled] = useState(intelligence.enabled);
  const [prompt, setPrompt] = useIntelligenceDraft(conversationScope);
  const [surface, setView] = useIntelligenceSurface(conversationScope);
  const [pending, setPending] = useState(false);
  const [turns, setTurns] = useState<IntelligenceConversationTurn[]>([]);
  const pageRef = useRef<HTMLElement>(null);
  const conversationEndRef = useRef<HTMLDivElement>(null);
  const scrollToLatestRef = useRef(false);
  const requestAbortRef = useRef<AbortController | undefined>(undefined);
  const historyAbortRef = useRef<AbortController | undefined>(undefined);
  const historyLoadSequenceRef = useRef(0);
  useEffect(() => {
    setEnabled(true);
    if (!intelligence.enabled) void ports.setIntelligenceEnabled(true);
  }, [intelligence.enabled, ports.setIntelligenceEnabled]);
  useEffect(() => {
    requestAbortRef.current?.abort(); historyAbortRef.current?.abort(); setTurns([]); setPending(false);
    const controller = new AbortController();
    historyAbortRef.current = controller;
    const loadSequence = ++historyLoadSequenceRef.current;
    if (ports.loadIntelligenceHistory && enabled && intelligence.status !== 'unavailable') {
      void ports.loadIntelligenceHistory(`ui-history-consent:${crypto.randomUUID()}`, controller.signal)
        .then((history) => {
          if (!controller.signal.aborted && loadSequence === historyLoadSequenceRef.current) {
            const savedTurns = savedIntelligenceTurns(history, contextKind);
            // A user can arrive from Home with a queued question before the
            // saved conversation finishes loading. Keep that live turn; the
            // history response describes an earlier server snapshot and must
            // never erase work that started after the request was made.
            setTurns((current) => current.length > 0 ? [...savedTurns, ...current] : savedTurns);
          }
        }).catch((cause) => {
          if (controller.signal.aborted || loadSequence !== historyLoadSequenceRef.current) return;
          setTurns([{ id: crypto.randomUUID(), question: 'Saved conversation', state: 'failed',
            error: presentAssistantError(cause), activity: [{ label: 'Could not load the saved conversation', state: 'failed' }] }]);
        });
    }
    return () => { controller.abort(); if (historyAbortRef.current === controller) historyAbortRef.current = undefined; };
  }, [contextKind, conversationScope, enabled, intelligence.status, ports.loadIntelligenceHistory]);
  useEffect(() => () => requestAbortRef.current?.abort(), []);
  useLayoutEffect(() => {
    if (!ios) return;
    const page = pageRef.current;
    if (!page) return;
    let frame = 0;
    const syncPinnedState = () => {
      const headerHeight = document.querySelector<HTMLElement>('.account-header')?.getBoundingClientRect().height ?? 0;
      page.classList.toggle('am-intelligence-page--pinned', page.getBoundingClientRect().top <= headerHeight + 1);
    };
    const fitToViewport = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const productContent = page.closest<HTMLElement>('.product-content');
        const bottomPadding = productContent
          ? Number.parseFloat(window.getComputedStyle(productContent).paddingBottom) || 0 : 0;
        const headerHeight = document.querySelector<HTMLElement>('.account-header')?.getBoundingClientRect().height ?? 0;
        const pageTop = page.getBoundingClientRect().top + window.scrollY;
        const startingHeight = Math.max(160, Math.floor(window.innerHeight - pageTop - bottomPadding));
        const expandedHeight = Math.max(startingHeight, Math.floor(window.innerHeight - headerHeight - bottomPadding));
        const tabs = page.querySelector<HTMLElement>('.am-intelligence-page__tabs');
        const panel = page.querySelector<HTMLElement>('.am-intelligence-page__panel');
        const content = surface.view === 'attention'
          ? page.querySelector<HTMLElement>('.am-intelligence-page__body > .am-intelligence-overlay__attention')
          : page.querySelector<HTMLElement>('.am-intelligence-page__conversation .am-intelligence-overlay__turns');
        const dock = surface.view === 'ask' ? page.querySelector<HTMLElement>('.am-intelligence-page__dock') : null;
        const gap = Number.parseFloat(window.getComputedStyle(page).rowGap) || 0;
        const panelBorder = panel ? panel.offsetHeight - panel.clientHeight : 0;
        const neededHeight = content && tabs
          ? tabs.offsetHeight + gap + panelBorder + content.scrollHeight + (dock?.offsetHeight ?? 0)
          : startingHeight;
        page.style.setProperty('--am-intelligence-page-height',
          `${Math.min(expandedHeight, Math.max(startingHeight, Math.ceil(neededHeight)))}px`);
        syncPinnedState();
      });
    };
    fitToViewport();
    window.addEventListener('resize', fitToViewport);
    window.addEventListener('scroll', syncPinnedState, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(fitToViewport);
    if (page.parentElement) observer?.observe(page.parentElement);
    const dock = page.querySelector<HTMLElement>('.am-intelligence-page__dock');
    if (dock) observer?.observe(dock);
    return () => {
      window.cancelAnimationFrame(frame); window.removeEventListener('resize', fitToViewport);
      window.removeEventListener('scroll', syncPinnedState); observer?.disconnect();
    };
  }, [ios, surface.view, turns]);
  useEffect(() => {
    if (!ios) return;
    const page = pageRef.current;
    if (!page?.classList.contains('am-intelligence-page--pinned')) return;
    const feed = page.querySelector<HTMLElement>('.am-intelligence-page__conversation .am-intelligence-overlay__turns');
    if (feed) feed.scrollTop = feed.scrollHeight;
  }, [ios, pending, turns]);
  useEffect(() => {
    if (!scrollToLatestRef.current) return;
    conversationEndRef.current?.scrollIntoView?.({ block: 'nearest' });
    if (!pending) scrollToLatestRef.current = false;
  }, [pending, turns]);
  const clearDeletedTurns = () => {
    historyAbortRef.current?.abort(); historyLoadSequenceRef.current += 1; setTurns([]);
  };
  useIntelligenceHistoryDeletion(conversationScope, clearDeletedTurns);
  const send = async () => {
    const question = prompt.trim();
    if (!enabled || intelligence.status === 'unavailable' || pending || !question) return;
    const id = crypto.randomUUID();
    scrollToLatestRef.current = true;
    setTurns((current) => [...current, { id, question, state: 'working', activity: [
      { label: contextKind === 'PORTFOLIO' ? 'Confirmed I can use the practice summary available to you'
        : 'Confirmed I can use records from this tax profile', state: 'complete' },
      { label: contextKind === 'PORTFOLIO' ? 'Checking the permitted practice summary'
        : 'Checking the relevant records', state: 'working' },
    ] }]);
    setPrompt(''); setPending(true);
    const controller = new AbortController(); requestAbortRef.current = controller;
    try {
      const response = await ports.queryIntelligence(question, `ui-consent:${crypto.randomUUID()}`,
        controller.signal, 'PERSISTED');
      if (controller.signal.aborted) return;
      setTurns((current) => current.map((turn) => turn.id === id
        ? { ...turn, state: 'complete', response, activity: completedIntelligenceActivity(response, contextKind) }
        : turn));
    } catch (cause) {
      if (controller.signal.aborted) return;
      setTurns((current) => current.map((turn) => turn.id === id ? { ...turn, state: 'failed',
        error: presentAssistantError(cause), activity: [
          { label: contextKind === 'PORTFOLIO' ? 'Confirmed I can use the practice summary available to you'
            : 'Confirmed I can use records from this tax profile', state: 'complete' },
          { label: 'Could not finish checking the records', state: 'failed' },
        ] } : turn));
    } finally {
      if (requestAbortRef.current === controller) requestAbortRef.current = undefined;
      if (!controller.signal.aborted) setPending(false);
    }
  };
  const unavailable = intelligence.status === 'unavailable';
  const canSend = enabled && !unavailable && !pending && Boolean(prompt.trim());
  useEffect(() => {
    if (!surface.submitRequestId || surface.view !== 'ask' || !canSend) return;
    consumeIntelligenceSubmit(conversationScope, surface.submitRequestId);
    void send();
  }, [canSend, conversationScope, surface.submitRequestId, surface.view]);
  return <section aria-label="Amoye intelligence conversation"
    className={`am-intelligence-page${turns.length === 0 ? ' am-intelligence-page--empty' : ''}`} ref={pageRef}>
    <nav aria-label="Intelligence page views" className="am-evidence-view-tabs am-intelligence-page__tabs">
      <button aria-current={surface.view === 'ask' ? 'page' : undefined} onClick={() => setView('ask')} type="button">Ask</button>
      <button aria-current={surface.view === 'attention' ? 'page' : undefined} onClick={() => setView('attention')} type="button">
        Attention{attention.length > 0 ? ` (${attention.length})` : ''}
      </button>
    </nav>
    <Panel aria-label="Intelligence conversation panel" className="am-intelligence-page__panel">
      <div className="am-intelligence-page__body">
        {surface.view === 'attention' ? <IntelligenceAttentionList attention={attention} />
          : <div className="am-intelligence-page__ask">
            {turns.length > 0 && <div className="am-intelligence-page__conversation">
              <IntelligenceTurnFeed conversationEndRef={conversationEndRef}
                onNavigate={ports.openIntelligenceIntent} turns={turns} />
            </div>}
            <div className="am-intelligence-page__dock">
              <IntelligenceComposer canSend={canSend} disabled={unavailable} emptyPlaceholder="Ask about your taxes"
                followUpPlaceholder="Ask a follow-up" onPromptChange={setPrompt} onSend={() => void send()}
                pending={pending} prompt={prompt} turnsPresent={turns.length > 0} variant="page" />
              <p className="am-intelligence-page__disclaimer">{ios ? intelligenceDisclaimer : standardIntelligenceDisclaimer}</p>
            </div>
          </div>}
      </div>
    </Panel>
  </section>;
}

function followUpPrompt(turns: readonly IntelligenceConversationTurn[], question: string,
  contextKind: 'CLIENT' | 'PORTFOLIO'): string {
  const prior = turns.filter((turn) => turn.response).slice(-2).map((turn) =>
    `User: ${turn.question.slice(0, 300)}\nAmoye: ${turn.response!.answer.slice(0, 650)}`).join('\n\n');
  const context = contextKind === 'PORTFOLIO' ? 'authorised practice portfolio' : 'same authorised taxpayer context';
  return prior ? `Continue this read-only conversation within the ${context}.\n\n${prior}\n\nUser follow-up: ${question}` : question;
}

const intelligencePanelMinWidth = 320;
const intelligencePanelMaxWidth = 720;
const intelligencePanelDefaultWidth = 448;

function maximumIntelligencePanelWidth(): number {
  if (typeof window === 'undefined') return intelligencePanelMaxWidth;
  return Math.max(intelligencePanelMinWidth, Math.min(intelligencePanelMaxWidth, window.innerWidth - 32));
}

function constrainIntelligencePanelWidth(width: number): number {
  return Math.round(Math.min(maximumIntelligencePanelWidth(), Math.max(intelligencePanelMinWidth, width)));
}

function storedIntelligencePanelWidth(): number {
  if (typeof window === 'undefined') return intelligencePanelDefaultWidth;
  const stored = Number(window.localStorage.getItem('amoye-intelligence-panel-width'));
  return Number.isFinite(stored) && stored > 0 ? constrainIntelligencePanelWidth(stored) : intelligencePanelDefaultWidth;
}

/** Optional product-wide intelligence layer. It can explain and route a user
 * to an existing operation, but it owns no tax workflow state and exposes no
 * mutation or calculation tool to a model. */
export function IntelligenceOverlay({ attention, conversationScope, contextKind = 'CLIENT', intelligence,
  expanded = false, mobileSettingsOpen = false, onCloseMobileSettings, onExpand, ports }: {
  attention: readonly IntelligenceAttentionAction[];
  conversationScope: string;
  contextKind?: 'CLIENT' | 'PORTFOLIO';
  expanded?: boolean;
  intelligence: IntelligenceState;
  mobileSettingsOpen?: boolean;
  onCloseMobileSettings?: () => void;
  onExpand?: (view: IntelligenceSurfaceView) => void;
  ports: Pick<SharedJourneyPorts, 'setIntelligenceEnabled' | 'queryIntelligence'
    | 'loadIntelligenceHistory' | 'deleteIntelligenceHistory' | 'openIntelligenceIntent'>;
}) {
  const chromeHosts = useWorkspaceChromeHosts();
  const phoneNavigation = usePhoneNavigation();
  const ios = useIOSPresentation();
  const headerLaunchers = !ios && Boolean(chromeHosts?.headerIntelligence);
  const [enabled, setEnabled] = useState(intelligence.enabled);
  const [open, setOpen] = useState(false);
  const [mobilePanelRendered, setMobilePanelRendered] = useState(false);
  const [mobilePanelVisible, setMobilePanelVisible] = useState(false);
  const [surface, setView] = useIntelligenceSurface(conversationScope);
  const [prompt, setPrompt] = useIntelligenceDraft(conversationScope);
  const [consented, setConsented] = useState(intelligence.enabled);
  const historyPreferencesRef = useRef(new Map<string, boolean>([[conversationScope, true]]));
  const loadedHistoryScopesRef = useRef(new Set<string>());
  const historyLoadSequenceRef = useRef(0);
  const [persistHistory, setPersistHistory] = useState(true);
  const [pending, setPending] = useState(false);
  const [turns, setTurns] = useState<IntelligenceConversationTurn[]>([]);
  const [placement, setPlacement] = useState<'floating' | 'docked'>(() => typeof window !== 'undefined'
    && window.localStorage.getItem('amoye-intelligence-placement') === 'floating' ? 'floating' : 'docked');
  const [panelWidth, setPanelWidth] = useState(storedIntelligencePanelWidth);
  const [resizing, setResizing] = useState(false);
  const conversationEndRef = useRef<HTMLDivElement>(null);
  const enabledRef = useRef(intelligence.enabled);
  const requestAbortRef = useRef<AbortController | undefined>(undefined);
  const resizeCleanupRef = useRef<(() => void) | undefined>(undefined);
  const mobileLauncherRef = useRef<HTMLButtonElement>(null);
  const desktopLauncherRef = useRef<HTMLButtonElement | null>(null);
  const mobilePanelCloseRef = useRef<HTMLButtonElement>(null);
  const mobilePanelCloseTimerRef = useRef<number | undefined>(undefined);
  const mobilePanelRestoreFocusRef = useRef(false);
  const mobilePanelSwipeStartRef = useRef<{ x: number; y: number } | undefined>(undefined);
  const mobilePanelRequested = phoneNavigation && chromeHosts
    ? chromeHosts.activeMobileLayer === 'intelligence' : phoneNavigation && open;
  const assistantRendered = !expanded && (phoneNavigation ? mobilePanelRendered : open);
  const assistantVisible = !expanded && (phoneNavigation ? mobilePanelVisible : open);
  useEffect(() => {
    if (!expanded) return;
    setOpen(false);
    if (phoneNavigation && chromeHosts?.activeMobileLayer === 'intelligence') chromeHosts.closeMobileLayer('intelligence');
  }, [chromeHosts, expanded, phoneNavigation]);
  useEffect(() => {
    enabledRef.current = intelligence.enabled;
    setEnabled(intelligence.enabled);
    setConsented(intelligence.enabled);
    if (!intelligence.enabled) { requestAbortRef.current?.abort(); setPending(false); }
  }, [intelligence.enabled]);
  useEffect(() => {
    requestAbortRef.current?.abort(); setTurns([]); setPending(false);
    historyLoadSequenceRef.current += 1;
    setConsented(enabledRef.current);
    setPersistHistory(historyPreferencesRef.current.get(conversationScope) ?? true);
  }, [conversationScope]);
  useEffect(() => {
    if (!phoneNavigation) {
      if (mobilePanelCloseTimerRef.current) window.clearTimeout(mobilePanelCloseTimerRef.current);
      setMobilePanelVisible(false); setMobilePanelRendered(false);
      return;
    }
    if (mobilePanelCloseTimerRef.current) window.clearTimeout(mobilePanelCloseTimerRef.current);
    if (mobilePanelRequested) {
      setMobilePanelRendered(true);
      const frame = window.requestAnimationFrame(() => setMobilePanelVisible(true));
      return () => window.cancelAnimationFrame(frame);
    }
    setMobilePanelVisible(false);
    if (!mobilePanelRendered) return;
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    mobilePanelCloseTimerRef.current = window.setTimeout(() => {
      setMobilePanelRendered(false);
      if (mobilePanelRestoreFocusRef.current) window.requestAnimationFrame(() =>
        (headerLaunchers ? desktopLauncherRef.current ?? mobileLauncherRef.current : mobileLauncherRef.current)?.focus());
      mobilePanelRestoreFocusRef.current = false;
    }, reducedMotion ? 0 : 220);
  }, [headerLaunchers, mobilePanelRendered, mobilePanelRequested, phoneNavigation]);
  useEffect(() => {
    if (!assistantRendered) return;
    if (phoneNavigation) document.documentElement.classList.add('am-mobile-intelligence-is-open');
    if (phoneNavigation && assistantVisible) mobilePanelCloseRef.current?.focus();
    const close = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      mobilePanelRestoreFocusRef.current = phoneNavigation;
      if (phoneNavigation && chromeHosts) chromeHosts.closeMobileLayer('intelligence');
      else { setOpen(false); desktopLauncherRef.current?.focus(); }
    };
    window.addEventListener('keydown', close);
    return () => {
      document.documentElement.classList.remove('am-mobile-intelligence-is-open');
      window.removeEventListener('keydown', close);
    };
  }, [assistantRendered, assistantVisible, phoneNavigation]);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const docked = !phoneNavigation && open && placement === 'docked';
    document.documentElement.classList.toggle('am-intelligence-is-docked', docked);
    window.localStorage.setItem('amoye-intelligence-placement', placement);
    return () => document.documentElement.classList.remove('am-intelligence-is-docked');
  }, [open, placement, phoneNavigation]);
  useEffect(() => {
    document.documentElement.style.setProperty('--am-intelligence-panel-width', `${panelWidth}px`);
    window.localStorage.setItem('amoye-intelligence-panel-width', String(panelWidth));
  }, [panelWidth]);
  useEffect(() => {
    const fitToViewport = () => setPanelWidth((current) => constrainIntelligencePanelWidth(current));
    window.addEventListener('resize', fitToViewport);
    return () => {
      window.removeEventListener('resize', fitToViewport);
      resizeCleanupRef.current?.();
      if (mobilePanelCloseTimerRef.current) window.clearTimeout(mobilePanelCloseTimerRef.current);
      document.documentElement.classList.remove('am-intelligence-is-resizing');
      document.documentElement.classList.remove('am-mobile-intelligence-is-open');
      document.documentElement.style.removeProperty('--am-intelligence-panel-width');
    };
  }, []);
  useEffect(() => { conversationEndRef.current?.scrollIntoView?.({ block: 'nearest' }); }, [pending, turns]);
  const setMode = (next: boolean) => {
    enabledRef.current = next;
    setEnabled(next);
    setConsented(next);
    if (!next) {
      requestAbortRef.current?.abort(); setPending(false); setPrompt('');
      setTurns((current) => current.map((turn) => turn.state === 'working' ? { ...turn, state: 'failed',
        error: 'This request stopped because Amoye intelligence was turned off.', activity: [
          ...turn.activity.filter((activity) => activity.state !== 'working'),
          { label: 'Request stopped when intelligence was turned off', state: 'failed' },
        ] } : turn));
    }
    void ports.setIntelligenceEnabled(next);
  };
  const closeAssistant = (restoreFocus = true) => {
    mobilePanelRestoreFocusRef.current = phoneNavigation && restoreFocus;
    if (phoneNavigation && chromeHosts) chromeHosts.closeMobileLayer('intelligence');
    else { setOpen(false); if (restoreFocus) desktopLauncherRef.current?.focus(); }
  };
  const openMobileAssistant = () => {
    mobilePanelRestoreFocusRef.current = false;
    if (!enabled) setMode(true);
    setView('ask');
    if (chromeHosts) chromeHosts.openMobileLayer('intelligence');
    else setOpen(true);
  };
  useEffect(() => chromeHosts?.registerAsk?.(conversationScope, (question) => {
    setPrompt(question);
    if (!enabled) setMode(true);
    setView('ask');
    if (expanded) return;
    if (phoneNavigation && chromeHosts) chromeHosts.openMobileLayer('intelligence');
    else setOpen(true);
  }), [chromeHosts?.registerAsk, conversationScope, enabled, expanded, phoneNavigation, setPrompt]);
  const send = async () => {
    const question = prompt.trim();
    if (!enabled || intelligence.status === 'unavailable' || !consented || pending || !question) return;
    const id = crypto.randomUUID();
    const working: IntelligenceConversationTurn = { id, question, state: 'working', activity: [
      { label: contextKind === 'PORTFOLIO' ? 'Confirmed I can use the practice summary available to you'
        : 'Confirmed I can use records from this tax profile', state: 'complete' },
      { label: contextKind === 'PORTFOLIO' ? 'Checking the permitted practice summary'
        : 'Checking the relevant records', state: 'working' },
    ] };
    setTurns((current) => [...current, working]); setPrompt(''); setPending(true);
    const controller = new AbortController(); requestAbortRef.current = controller;
    try {
      const response = await ports.queryIntelligence(persistHistory ? question : followUpPrompt(turns, question, contextKind),
        `ui-consent:${crypto.randomUUID()}`, controller.signal, persistHistory ? 'PERSISTED' : 'EPHEMERAL');
      if (controller.signal.aborted) return;
      setTurns((current) => current.map((turn) => turn.id === id ? { ...turn, state: 'complete', response,
        activity: completedIntelligenceActivity(response, contextKind) } : turn));
    } catch (cause) {
      if (controller.signal.aborted) return;
      const message = presentAssistantError(cause);
      setTurns((current) => current.map((turn) => turn.id === id ? { ...turn, state: 'failed', error: message, activity: [
        { label: contextKind === 'PORTFOLIO' ? 'Confirmed I can use the practice summary available to you'
          : 'Confirmed I can use records from this tax profile', state: 'complete' },
        { label: 'Could not finish checking the records', state: 'failed' },
      ] } : turn));
    } finally {
      if (requestAbortRef.current === controller) requestAbortRef.current = undefined;
      if (!controller.signal.aborted) setPending(false);
    }
  };
  const canSend = enabled && intelligence.status !== 'unavailable' && consented && !pending && Boolean(prompt.trim());
  const beginResize = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    resizeCleanupRef.current?.();
    const startX = event.clientX;
    const startWidth = panelWidth;
    setResizing(true);
    document.documentElement.classList.add('am-intelligence-is-resizing');
    const move = (moveEvent: PointerEvent) => setPanelWidth(constrainIntelligencePanelWidth(startWidth + startX - moveEvent.clientX));
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
      document.documentElement.classList.remove('am-intelligence-is-resizing');
      setResizing(false);
      resizeCleanupRef.current = undefined;
    };
    resizeCleanupRef.current = stop;
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
  };
  const resizeWithKeyboard = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const increments: Record<string, number> = { ArrowLeft: 24, ArrowRight: -24 };
    if (event.key === 'Home') { event.preventDefault(); setPanelWidth(intelligencePanelMinWidth); return; }
    if (event.key === 'End') { event.preventDefault(); setPanelWidth(maximumIntelligencePanelWidth()); return; }
    const increment = increments[event.key];
    if (increment === undefined) return;
    event.preventDefault();
    setPanelWidth((current) => constrainIntelligencePanelWidth(current + increment));
  };
  const loadSavedHistory = async () => {
    if (!ports.loadIntelligenceHistory || loadedHistoryScopesRef.current.has(conversationScope)) return;
    loadedHistoryScopesRef.current.add(conversationScope);
    const loadSequence = ++historyLoadSequenceRef.current;
    const loadingScope = conversationScope;
    try {
      const history = await ports.loadIntelligenceHistory(`ui-history-consent:${crypto.randomUUID()}`);
      if (loadSequence !== historyLoadSequenceRef.current || loadingScope !== conversationScope) return;
      setTurns(savedIntelligenceTurns(history, contextKind));
    } catch (cause) {
      if (loadSequence !== historyLoadSequenceRef.current || loadingScope !== conversationScope) return;
      loadedHistoryScopesRef.current.delete(loadingScope);
      setTurns((current) => [...current, { id: crypto.randomUUID(), question: 'Saved conversation',
        state: 'failed', error: presentAssistantError(cause),
        activity: [{ label: 'Could not load the saved conversation', state: 'failed' }] }]);
    }
  };
  useEffect(() => {
    const savedForCurrentContext = historyPreferencesRef.current.get(conversationScope) ?? true;
    if (assistantRendered && surface.view === 'ask' && persistHistory && savedForCurrentContext && consented && enabled) {
      void loadSavedHistory();
    }
  }, [assistantRendered, consented, conversationScope, enabled, persistHistory, surface.view]);
  const changeHistoryMode = async (checked: boolean) => {
    historyPreferencesRef.current.set(conversationScope, checked);
    setPersistHistory(checked);
    if (checked) await loadSavedHistory();
  };
  const deleteHistory = async () => {
    await ports.deleteIntelligenceHistory?.(`ui-history-delete:${crypto.randomUUID()}`);
    setTurns([]);
    publishIntelligenceHistoryDeletion(conversationScope);
  };
  const clearDeletedTurns = () => {
    historyLoadSequenceRef.current += 1; loadedHistoryScopesRef.current.delete(conversationScope); setTurns([]);
  };
  useIntelligenceHistoryDeletion(conversationScope, clearDeletedTurns);
  const mobileSettingsControls = <IntelligenceSettings contextKind={contextKind} consented={consented}
    enabled={enabled} intelligenceStatus={intelligence.status} onConsentChange={setConsented}
    onDeleteHistory={ports.deleteIntelligenceHistory ? deleteHistory : undefined}
    onEnabledChange={setMode} onHistoryChange={changeHistoryMode} persistHistory={persistHistory} showEnabledControl />;
  const mobileLauncher = <button aria-expanded={assistantVisible}
    aria-label={`Open Ask Amoye and Attention${enabled && attention.length > 0 ? `, ${attention.length} attention item${attention.length === 1 ? '' : 's'}` : ''}`}
    className="account-header__mobile-action account-header__mobile-intelligence-button" onClick={openMobileAssistant}
    ref={mobileLauncherRef}
    title="Ask Amoye and Attention" type="button"><Icon name="sparkles" size={20} />
    {enabled && attention.length > 0 && <strong>{attention.length}</strong>}</button>;
  const headerControls = <>
    <button aria-label="Search or ask Amoye" aria-expanded={assistantVisible && surface.view === 'ask'}
      className="am-header-iconbox" title="Search or ask Amoye" type="button" ref={mobileLauncherRef}
      onClick={(event) => {
        desktopLauncherRef.current = event.currentTarget;
        if (!enabled) setMode(true);
        if (expanded) { setView('ask'); return; }
        if (assistantVisible && surface.view === 'ask') { closeAssistant(); return; }
        setView('ask');
        if (phoneNavigation && chromeHosts) chromeHosts.openMobileLayer('intelligence'); else setOpen(true);
      }}><Icon name="search" size={18} /></button>
    <button aria-label={`Attention${enabled && attention.length > 0 ? `, ${attention.length} item${attention.length === 1 ? '' : 's'}` : ''}`}
      aria-expanded={assistantVisible && surface.view === 'attention'} className="am-header-iconbox" title="Attention" type="button"
      onClick={(event) => {
        desktopLauncherRef.current = event.currentTarget;
        if (!enabled) setMode(true);
        if (expanded) { setView('attention'); return; }
        if (assistantVisible && surface.view === 'attention') { closeAssistant(); return; }
        setView('attention');
        if (phoneNavigation && chromeHosts) chromeHosts.openMobileLayer('intelligence'); else setOpen(true);
      }}><Icon name="bell" size={18} />{enabled && attention.length > 0 && <span aria-hidden="true"
        className="am-header-iconbox__count">{attention.length}</span>}</button>
  </>;
  const shell = <><div className={`am-intelligence-overlay am-intelligence-overlay--${placement}`}
    style={{ '--am-intelligence-panel-width': `${panelWidth}px` } as CSSProperties}>
    {!headerLaunchers && phoneNavigation && !chromeHosts?.mobileIntelligence && <div className="am-intelligence-overlay__mobile-launcher">{mobileLauncher}</div>}
    {!headerLaunchers && !phoneNavigation && <div aria-label="Amoye intelligence" className="am-intelligence-overlay__launchers am-intelligence-overlay__launchers--desktop" role="group">
      <button aria-expanded={open && surface.view === 'ask'} onClick={() => { if (!enabled) setMode(true); setView('ask'); if (!expanded) setOpen((current) => surface.view === 'ask' ? !current : true); }} type="button">
        <Icon name="sparkles" size={17} /><span>Ask</span>
      </button>
      <button aria-expanded={open && surface.view === 'attention'} aria-label={`Attention${enabled && attention.length > 0 ? `, ${attention.length} item${attention.length === 1 ? '' : 's'}` : ''}`}
        onClick={() => { if (!enabled) setMode(true); setView('attention'); if (!expanded) setOpen((current) => surface.view === 'attention' ? !current : true); }} type="button">
        <span className="am-intelligence-overlay__launcher-icon"><Icon name="warning" size={17} />
          {enabled && attention.length > 0 && <strong>{attention.length}</strong>}</span><span>Attention</span>
      </button>
    </div>}
    {assistantRendered && <aside aria-label="Amoye intelligence assistant"
      className={`am-intelligence-overlay__panel${phoneNavigation ? mobilePanelVisible ? ' is-open' : ' is-closing' : ''}`}
      onPointerDown={(event) => {
        if (phoneNavigation) mobilePanelSwipeStartRef.current = { x: event.clientX, y: event.clientY };
      }} onPointerUp={(event) => {
        const start = mobilePanelSwipeStartRef.current; mobilePanelSwipeStartRef.current = undefined;
        if (!start) return;
        const horizontal = event.clientX - start.x; const vertical = event.clientY - start.y;
        if (vertical > 72 && Math.abs(vertical) > Math.abs(horizontal) * 1.2) closeAssistant();
      }}>
      <div aria-label="Resize Amoye intelligence panel" aria-orientation="vertical" aria-valuemax={maximumIntelligencePanelWidth()}
        aria-valuemin={intelligencePanelMinWidth} aria-valuenow={panelWidth}
        aria-valuetext={`${resizing ? 'Resizing, ' : ''}${panelWidth} pixels wide`} className="am-intelligence-overlay__resize-handle"
        onKeyDown={resizeWithKeyboard} onPointerDown={beginResize} role="separator" tabIndex={0} />
      <header>{phoneNavigation ? <button aria-label="Close Ask Amoye" className="am-intelligence-overlay__back"
        onClick={() => closeAssistant()} ref={mobilePanelCloseRef} type="button"><Icon name="chevron-down" size={22} /></button> : null}
      <nav aria-label="Intelligence views" className="am-intelligence-overlay__tabs">
        <button aria-current={surface.view === 'ask' ? 'page' : undefined} onClick={() => setView('ask')} type="button">Ask</button>
        <button aria-current={surface.view === 'attention' ? 'page' : undefined} onClick={() => setView('attention')} type="button">Attention{enabled && attention.length > 0 ? ` (${attention.length})` : ''}</button>
      </nav>
        {!phoneNavigation && <div className="am-intelligence-overlay__window-controls">
          <button aria-label={placement === 'docked' ? 'Float Amoye intelligence' : 'Dock Amoye intelligence on the right'}
            className="am-intelligence-tooltip" data-tooltip={placement === 'docked' ? 'Use floating view' : 'Dock on the right'}
            onClick={() => setPlacement((current) => current === 'docked' ? 'floating' : 'docked')} type="button">
            <Icon name="panel-right" size={18} /></button>
          {onExpand && <button aria-label="Maximize Amoye intelligence" className="am-intelligence-tooltip"
            data-tooltip="Open full Intelligence page" onClick={() => { closeAssistant(false); onExpand(surface.view); }} type="button">
            <Icon name="arrow-up-right" size={18} /></button>}
          <button aria-label="Close Amoye intelligence" onClick={() => closeAssistant()} type="button"><Icon name="close" size={18} /></button>
        </div>}</header>
      {!enabled ? <EmptyState compact title="Intelligence is off"
        description="Ask Amoye, follow-up questions and attention guidance are disabled. Your tax workflows remain unchanged." />
        : intelligence.status === 'unavailable' ? <ErrorState compact title="Guidance is temporarily unavailable"
          description={ios ? 'Continue with the existing evidence, return and filing screens; they do not depend on intelligence.'
            : 'Continue with your documents, returns and filing; they do not depend on intelligence.'} />
          : surface.view === 'attention' ? <IntelligenceAttentionList attention={attention}
            onOpen={() => { if (phoneNavigation) closeAssistant(false); }} />
            : <div className="am-intelligence-overlay__conversation">
            <IntelligenceTurnFeed conversationEndRef={conversationEndRef} onNavigate={async (intent) => {
              const opened = await ports.openIntelligenceIntent?.(intent);
              if (opened !== false && phoneNavigation) closeAssistant(false);
              return opened;
            }} turns={turns} />
          </div>}
      {surface.view === 'ask' && <IntelligenceComposer canSend={canSend}
        disabled={!enabled || intelligence.status === 'unavailable'}
        emptyPlaceholder="Ask about your taxes"
        followUpPlaceholder="Ask a follow-up"
        onPromptChange={setPrompt} onSend={() => void send()} pending={pending} prompt={prompt}
        rows={phoneNavigation ? 1 : 2} turnsPresent={turns.length > 0} />}
      <footer className="am-intelligence-overlay__footer"><span>{ios ? intelligenceDisclaimer : standardIntelligenceDisclaimer}</span></footer>
    </aside>}
  </div>{headerLaunchers && chromeHosts?.headerIntelligence ? createPortal(headerControls, chromeHosts.headerIntelligence)
    : phoneNavigation && chromeHosts?.mobileIntelligence ? createPortal(mobileLauncher, chromeHosts.mobileIntelligence) : null}</>;
  const settingsPage = mobileSettingsOpen ? <MobileSettingsPage onBack={onCloseMobileSettings}>{mobileSettingsControls}</MobileSettingsPage> : null;
  return <>{settingsPage}{typeof document === 'undefined' ? shell : createPortal(shell, document.body)}</>;
}

function IntelligenceSettings({ consented, contextKind, enabled, intelligenceStatus, onConsentChange,
  onDeleteHistory, onEnabledChange, onHistoryChange, persistHistory, showEnabledControl }: {
  consented: boolean;
  contextKind: 'CLIENT' | 'PORTFOLIO';
  enabled: boolean;
  intelligenceStatus: IntelligenceState['status'];
  onConsentChange(value: boolean): void;
  onDeleteHistory?: () => Promise<void>;
  onEnabledChange(value: boolean): void;
  onHistoryChange(value: boolean): Promise<void>;
  persistHistory: boolean;
  showEnabledControl: boolean;
}) {
  return <section aria-label="Amoye intelligence settings" className="am-intelligence-overlay__settings">
    <p>These preferences apply only to this {contextKind === 'PORTFOLIO' ? 'practice view' : 'tax profile'}. If you switch context, Amoye uses that context’s own setting.</p>
    {showEnabledControl && <label className="am-intelligence-setting"><input checked={enabled}
      onChange={(event) => onEnabledChange(event.target.checked)} type="checkbox" />
      <span><strong>Amoye intelligence</strong><small>Keep Ask Amoye and Attention available in this product. It is enabled by default.</small></span></label>}
    <label className="am-intelligence-setting"><input checked={consented} disabled={!enabled}
      onChange={(event) => onConsentChange(event.target.checked)} type="checkbox" />
      <span><strong>Use records available to me</strong><small>{contextKind === 'PORTFOLIO'
        ? 'Let Ask Amoye use the limited practice summary you are allowed to see.'
        : 'Let Ask Amoye use records from this tax profile.'}</small></span></label>
    <label className="am-intelligence-setting"><input checked={persistHistory} disabled={!enabled || !consented}
      onChange={(event) => void onHistoryChange(event.target.checked)} type="checkbox" />
      <span><strong>Save conversation for 30 days</strong><small>{contextKind === 'PORTFOLIO'
        ? 'On by default. This conversation stays in this practice view and never carries into a selected client.'
        : 'On by default. This conversation stays in this tax profile and never carries into another taxpayer or client.'}</small></span></label>
    {persistHistory && onDeleteHistory && <Button onClick={() => void onDeleteHistory()} size="small" tone="secondary"
      type="button">Delete saved conversation</Button>}
    {showEnabledControl && intelligenceStatus === 'unavailable' && (
      <small>Guidance is temporarily unavailable. Your tax workflow is unaffected.</small>
    )}
  </section>;
}

function MobileSettingsPage({ children, onBack }: { children: ReactNode; onBack?: () => void }) {
  const account = useWorkspaceAccount();
  return <section aria-label="Settings" className="am-mobile-settings-page">
    <header><button aria-label="Back to previous tax page" onClick={onBack} type="button"><Icon name="chevron-left" size={22} /></button>
      <div><span>Settings</span><strong>Account and preferences</strong></div></header>
    <div className="am-mobile-settings-page__content">
      <Panel title="Profile" description="The signed-in account used across your entitled Amoye products.">
        <dl className="am-mobile-settings-page__summary"><div><dt>Name</dt><dd>{account?.displayName ?? 'Not available'}</dd></div>
          <div><dt>Email</dt><dd>{account?.email ?? 'Not available'}</dd></div><div><dt>Current role</dt><dd>{account?.capacity ?? 'Not available'}</dd></div></dl>
        <p className="am-mobile-settings-page__notice">Profile updates require a secured account-management API and are not available in this version.</p>
      </Panel>
      <Panel title="Security" description="Password and session controls must be verified by the account service.">
        <dl className="am-mobile-settings-page__summary"><div><dt>Password</dt><dd>Protected</dd></div>
          <div><dt>Current session</dt><dd>Signed in on this browser</dd></div></dl>
        <p className="am-mobile-settings-page__notice">Changing your password and managing signed-in devices are not available until the secured account APIs are connected.</p>
        {account && <Button onClick={account.onSignOut} tone="danger" type="button">Sign out</Button>}
      </Panel>
      <Panel title="Appearance" description="Choose how Amoye looks on this browser.">
        <ThemePreferenceSelector />
      </Panel>
      <Panel title="Amoye" description="Control optional read-only assistance for the current authorized context.">{children}</Panel>
    </div>
  </section>;
}

/** Reserved for a future network-aware implementation. It is intentionally not rendered today. */
export function LowDataModeControl({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) {
  return <Toggle checked={checked} description="Reduce automatic refreshes and defer document previews on slow or expensive connections. This mode is not available yet."
    label="Low-data mode" onChange={(event) => onChange(event.target.checked)} />;
}
